/**
 * Custom bundle builder: multi-step Swiper selection, tiered discounts, Biscuits bundle URL handoff or app bridge.
 */

/** Theme sticky stack height in px (header + announcement + product bar flags). Shared by stepper layout and step scroll-into-view. */
function cbbReadThemeHeaderStackPx() {
  if (typeof document === "undefined") return 0;
  const st = getComputedStyle(document.documentElement);
  const num = (name) => {
    const raw = st.getPropertyValue(name).trim();
    const v = parseFloat(raw);
    return Number.isFinite(v) ? v : 0;
  };
  return Math.max(
    0,
    num("--header-height") * num("--enable-sticky-header") +
    num("--announcement-bar-height") * num("--enable-sticky-announcement-bar") +
    num("--product-sticky-bar-height")
  );
}

/** Visible sticky chrome only (excludes header / announcement when theme hide-on-scroll is active). */
function cbbReadVisibleThemeHeaderStackPx() {
  if (typeof document === "undefined") return 0;
  const st = getComputedStyle(document.documentElement);
  const num = (name) => {
    const raw = st.getPropertyValue(name).trim();
    const v = parseFloat(raw);
    return Number.isFinite(v) ? v : 0;
  };
  const headerHidden = document.querySelector(".header--hidden") != null;
  const announcementHidden = document.querySelector(".announcement-bar--hidden") != null;

  let stack = num("--product-sticky-bar-height");
  if (!headerHidden) {
    stack += num("--header-height") * num("--enable-sticky-header");
  }
  if (!announcementHidden) {
    stack += num("--announcement-bar-height") * num("--enable-sticky-announcement-bar");
  }
  return Math.max(0, stack);
}

/**
 * Layout vs visual viewport (iOS Safari URL bar sits in the layout viewport and is not part of safe-area-inset).
 * `getBoundingClientRect` / `position: sticky; bottom` use the layout viewport.
 */
function cbbReadViewportBox() {
  const layoutH = typeof window !== "undefined" ? window.innerHeight || 0 : 0;
  const vv = typeof window !== "undefined" ? window.visualViewport : null;
  const offsetTop = vv ? vv.offsetTop || 0 : 0;
  const height = vv && vv.height ? vv.height : layoutH;
  const bottom = offsetTop + height;
  return {
    layoutH,
    offsetTop,
    height,
    bottom,
    urlBarOverlap: Math.max(0, Math.round(layoutH - bottom)),
  };
}

/**
 * Sticky stepper hero: scroll arms, resize debounce, min-height easing (`#bindStepperStickyHero` and helpers).
 * Single source of truth for timing/threshold tweaks.
 */
const CBB_STEPPER_STICKY = Object.freeze({
  /**
   * Post-hero arm sentinel `getBoundingClientRect().top` vs `cbbReadThemeHeaderStackPx()` (CSS sticky `top`).
   * Arm when: `top <= stack + armEnterPx - armExtraScrollPx` (extra scroll after the block clears the stack line).
   */
  armEnterPx: 6,
  /** Extra viewport scroll (px) after the enter line before arming — avoids sticky the instant the sentinel touches the threshold. */
  armExtraScrollPx: 150,
  /** Disarm when scrolled back up: `top > stack + armReleasePx` (hysteresis avoids arm/disarm flutter). */
  armReleasePx: 44,
  armOnDelayMs: 260,
  armOffDelayMs: 220,
  slideInPrepHoldMs: 160,
  unstickHoldMs: 120,
  resizeDebounceMs: 120,
  motionEnterFallbackMs: 900,
  /** Hero flow lock: match `--sticky-live-motion-enter` → `.stepper-hero__motion` in CSS (parallel motion + layout). */
  minHeightEaseS: 0.62,
  minHeightEaseCssBezier: "0.2, 0.85, 0.32, 1",
  minHeightEaseFallbackMs: 750,
  introGapPx: 18,
});

/** Collapse the mobile intro accordion after this much window scroll (inner slot scroll does not fire `window`). */
const CBB_INTRO_ACCORDION_PAGE_SCROLL_PX = 28;

if (!customElements.get("custom-bundle-builder-v2")) {
  customElements.define(
    "custom-bundle-builder-v2",
    class CustomBundleBuilderV2 extends HTMLElement {
      #config = null;
      /** @type {Map<string, { priceCents: number; stepIndex: number; quantity: number; imageUrl?: string; imageAlt?: string; title?: string }>} */
      #selection = new Map();
      /** After adding a bundle unit, scroll the tier strip to the last filled tile. */
      #tierScrollPending = false;
      /** @type {Map<number, Map<string, string>>} Per-step topic filter selection by group key (primary | secondary): id is all | 1–6 */
      #topicFilterGroupsByStep = new Map();
      /** @type {Map<number, object | null>} */
      #swipers = new Map();

      /** @type {number} */
      #currentStep = 1;
      /** Highest step index the user has opened (via Continue or stepper); allows returning after jumping back. */
      #furthestVisitedStep = 1;

      #onClickBound = null;
      #onKeyBound = null;
      #onDeliveryDelayOptionChangeBound = null;
      /** @type {((event: Event) => void) | null} */
      #onFreebiesLayoutChangedBound = null;
      /** @type {MutationObserver | null} */
      #biscuitsMountObserver = null;
      #onBiscuitsFormReadyBound = null;
      /** @type {MutationObserver | null} */
      #biscuitsSubmitAtcObserver = null;
      /** @type {ReturnType<typeof setTimeout> | null} */
      #biscuitsSubmitSafetyTimer = null;
      /** @type {ReturnType<typeof setTimeout> | null} */
      #biscuitsSubmitFallbackTimer = null;

      /** @type {MediaQueryList | null} */
      #narrowLayoutMq = null;
      #onNarrowLayoutMqChangeBound = null;

      /** @type {((e?: Event) => void) | null} */
      #onWindowScrollOrResizeBound = null;
      /** @type {number | null} */
      #introSlotStuckRaf = null;
      /** `window.scrollY` when the intro accordion last opened (page-scroll collapse). */
      #introAccordionOpenScrollY = 0;
      /** Ignore page-scroll collapse until this timestamp (layout / URL-bar settle after open). */
      #introAccordionIgnorePageScrollUntil = 0;

      /** @type {boolean} */
      #introAccordionOutsidePointerAttached = false;
      /** @type {((e: PointerEvent) => void) | null} */
      #introAccordionOutsidePointerBound = null;

      /** @type {ReturnType<typeof setTimeout> | null} */
      #selectionPersistTimer = null;
      /** @type {ReturnType<typeof setTimeout> | null} */
      #bundleUrlUpdateTimer = null;
      /** @type {ReturnType<typeof setTimeout> | null} */
      #copyBundleLinkStatusTimer = null;
      /** After sessionStorage rehydrate, sync selection into Biscuits once hosts exist */
      #restoredSelectionNeedsBiscuitsSync = false;

      /** @type {ResizeObserver | null} Updates stack height on layout, avoids scroll jitter */
      #stepperHeroResizeObserver = null;
      /** @type {ReturnType<typeof setTimeout> | null} Coalesce hero resize to reduce sticky arm / layout thrash. */
      #stepperHeroResizeDebounceTimer = null;
      /** @type {number | null} */
      #stepperStickyLayoutRaf = null;
      /** @type {number | null} Coalesced scroll read for arm state (IO + layout height changes caused sticky flicker). */
      #stepperStickyArmRaf = null;
      /** @type {ReturnType<typeof setTimeout> | null} Brief delay before arming sticky-live (smoother, fewer false toggles). */
      #stepperStickyArmOnTimer = null;
      /** @type {ReturnType<typeof setTimeout> | null} After prep (compact bar off-screen), remove prep to run slide-in transition. */
      #stepperStickySlideInPrepTimer = null;
      /** @type {ReturnType<typeof setTimeout> | null} Fallback: remove `sticky-live-motion-enter` if transitionend misses. */
      #stepperStickyMotionEnterFallbackTimer = null;
      /** @type {ReturnType<typeof setTimeout> | null} Fallback: hero `min-height` ease down to compact bar after slide-in. */
      #stepperHeroMinHeightEaseTimer = null;
      /** @type {(() => void) | null} Detach hero `min-height` transitionend listener. */
      #stepperHeroMinHeightEaseListenerCleanup = null;
      /** @type {ReturnType<typeof setTimeout> | null} Debounce disarm near the scroll threshold (reduces arm/disarm flicker). */
      #stepperStickyArmOffTimer = null;
      /** @type {string} Last applied sticky layout signature — skip redundant `--cbb-*` writes. */
      #stickyLayoutCacheKey = "";
      /** @type {ReturnType<typeof setTimeout> | null} Remove `--sticky-unstick-prep` so expanded copy/padding can transition in. */
      #stepperStickyUnstickExpandTimer = null;
      /** @type {(() => void) | null} */
      #stepperStickyOnWinResizeBound = null;
      /** @type {(() => void) | null} */
      #stepperStickyOnScrollBound = null;

      constructor() {
        super();
        this.#onClickBound = this.#onClick.bind(this);
        this.#onKeyBound = this.#onKeyDown.bind(this);
        this.#onDeliveryDelayOptionChangeBound = this.#onDeliveryDelayOptionChange.bind(this);
        this.#onFreebiesLayoutChangedBound = this.#onFreebiesLayoutChanged.bind(this);
        this.#onBiscuitsFormReadyBound = this.#onBiscuitsFormReady.bind(this);
        this.#onNarrowLayoutMqChangeBound = () => {
          this.#onNarrowBundleLayoutChange();
        };
        this.#onWindowScrollOrResizeBound = (e) => {
          if (e && e.type === "scroll" && e.currentTarget === window) {
            this.#collapseIntroAccordionOnPageScroll();
          }
          this.#scheduleIntroActionsSlotStuckUpdate();
        };
        this.#stepperStickyOnWinResizeBound = () => {
          this.#scheduleStepperStickyArmFromScroll();
          this.#scheduleStepperStickyLayout();
        };
        this.#stepperStickyOnScrollBound = () => {
          this.#scheduleStepperStickyArmFromScroll();
        };
        this.#introAccordionOutsidePointerBound = this.#onIntroAccordionOutsidePointerDown.bind(this);
      }

      connectedCallback() {
        this.#config = this.#readConfig();
        this.#bindStepperStickyHero();

        if (!this.#config || this.#config.stepMinPicks.length === 0) {
          const cta = this.querySelector("[data-cbb-primary-cta]");
          if (cta) cta.setAttribute("disabled", "");
          if (this.#biscuitsBridgeActive()) {
            this.#dismissAppLoader();
          }
          return;
        }

        this.addEventListener("click", this.#onClickBound);
        this.addEventListener("keydown", this.#onKeyBound);
        this.addEventListener("change", this.#onDeliveryDelayOptionChangeBound);
        this.addEventListener("cbb:freebies-layout-changed", this.#onFreebiesLayoutChangedBound);

        if (typeof document !== "undefined" && this.#introAccordionOutsidePointerBound && !this.#introAccordionOutsidePointerAttached) {
          document.addEventListener("pointerdown", this.#introAccordionOutsidePointerBound, true);
          this.#introAccordionOutsidePointerAttached = true;
        }

        this.#dockIntroActionsUnderSummary();

        this.#loadPersistedSelection();
        this.#applyBundleQueryParamPrefill();

        this.#syncIntroAccordionLayout();

        this.#scheduleInitSwiper(this.#currentStep);
        this.#refreshUI();

        if (this.#biscuitsBridgeActive()) {
          this.#attachBiscuitsBridge();
        }

        if (typeof window !== "undefined" && typeof window.matchMedia === "function") {
          this.#narrowLayoutMq = window.matchMedia("(max-width: 989px)");
          try {
            this.#narrowLayoutMq.addEventListener("change", this.#onNarrowLayoutMqChangeBound);
          } catch (e) {
            this.#narrowLayoutMq.addListener?.(this.#onNarrowLayoutMqChangeBound);
          }
        }

        this.#bindIntroActionsSlotStuckTracking();
      }

      disconnectedCallback() {
        if (this.#narrowLayoutMq && this.#onNarrowLayoutMqChangeBound) {
          try {
            this.#narrowLayoutMq.removeEventListener("change", this.#onNarrowLayoutMqChangeBound);
          } catch (e) {
            this.#narrowLayoutMq.removeListener?.(this.#onNarrowLayoutMqChangeBound);
          }
          this.#narrowLayoutMq = null;
        }

        this.#unbindIntroActionsSlotStuckTracking();

        this.#dockIntroActionsUnderSummary(true);
        if (typeof document !== "undefined" && this.#introAccordionOutsidePointerBound && this.#introAccordionOutsidePointerAttached) {
          document.removeEventListener("pointerdown", this.#introAccordionOutsidePointerBound, true);
          this.#introAccordionOutsidePointerAttached = false;
        }
        this.removeEventListener("click", this.#onClickBound);
        this.removeEventListener("keydown", this.#onKeyBound);
        this.removeEventListener("change", this.#onDeliveryDelayOptionChangeBound);
        this.removeEventListener("cbb:freebies-layout-changed", this.#onFreebiesLayoutChangedBound);
        document.removeEventListener("BiscuitsBundleForm:ready", this.#onBiscuitsFormReadyBound);
        if (this.#biscuitsMountObserver) {
          this.#biscuitsMountObserver.disconnect();
          this.#biscuitsMountObserver = null;
        }
        this.#clearBiscuitsSubmitLoadingUi();
        this.#destroySwipers();
        this.#unbindStepperStickyHero();

        if (this.#selectionPersistTimer != null) {
          clearTimeout(this.#selectionPersistTimer);
          this.#selectionPersistTimer = null;
        }
        if (this.#bundleUrlUpdateTimer != null) {
          clearTimeout(this.#bundleUrlUpdateTimer);
          this.#bundleUrlUpdateTimer = null;
        }
        if (this.#copyBundleLinkStatusTimer != null) {
          clearTimeout(this.#copyBundleLinkStatusTimer);
          this.#copyBundleLinkStatusTimer = null;
        }
      }

      #isNarrowBundleLayout() {
        return (
          typeof window !== "undefined" &&
          typeof window.matchMedia === "function" &&
          window.matchMedia("(max-width: 989px)").matches
        );
      }

      /** Stacked sidebar + mobile swiper placement (≤989px ↔ theme `.custom-bundle-builder-v2__layout` at min-width 990px). */
      #onNarrowBundleLayoutChange() {
        this.#dockIntroActionsUnderSummary(false);
        this.#resetIntroAccordionPanel();
        this.#syncIntroAccordionLayout();
        this.#scheduleIntroActionsSlotStuckUpdate();
        const step = this.#currentStep;
        this.#destroySwipers();
        this.#applyStepperLayout();
        this.#scheduleInitSwiper(step);
        this.#scheduleStepperStickyArmFromScroll();
        this.#scheduleStepperStickyLayout();
        this.#updateCta();
      }

      /* ===== Sticky stepper hero: `CBB_STEPPER_STICKY`, `#easeStepperHeroFlowLock`, layout CSS vars ===== */

      /** Sticky stepper: post-hero arm sentinel + hysteresis (no pinned shadow / IO). */
      #bindStepperStickyHero() {
        if (this.dataset.stepperSticky !== "true") return;

        const hero = this.querySelector("[data-cbb-stepper-hero]");
        const armSentinel = this.querySelector("[data-cbb-stepper-arm-sentinel]");
        if (!(hero instanceof HTMLElement) || !(armSentinel instanceof HTMLElement)) return;

        this.#unbindStepperStickyHero();

        if (typeof ResizeObserver !== "undefined") {
          this.#stepperHeroResizeObserver = new ResizeObserver(() => {
            if (this.#stepperHeroResizeDebounceTimer != null) {
              clearTimeout(this.#stepperHeroResizeDebounceTimer);
            }
            this.#stepperHeroResizeDebounceTimer = setTimeout(() => {
              this.#stepperHeroResizeDebounceTimer = null;
              this.#stickyLayoutCacheKey = "";
              this.#scheduleStepperStickyArmFromScroll();
              this.#scheduleStepperStickyLayout();
            }, CBB_STEPPER_STICKY.resizeDebounceMs);
          });
          this.#stepperHeroResizeObserver.observe(hero);
        }

        if (typeof window !== "undefined") {
          if (this.#stepperStickyOnScrollBound) {
            window.addEventListener("scroll", this.#stepperStickyOnScrollBound, { passive: true });
          }
          if (this.#stepperStickyOnWinResizeBound) {
            window.addEventListener("resize", this.#stepperStickyOnWinResizeBound, { passive: true });
          }
        }

        requestAnimationFrame(() => {
          this.#scheduleStepperStickyArmFromScroll();
          this.#scheduleStepperStickyLayout();
        });
      }

      #unbindStepperStickyHero() {
        if (this.#stepperHeroResizeObserver) {
          this.#stepperHeroResizeObserver.disconnect();
          this.#stepperHeroResizeObserver = null;
        }
        if (this.#stepperHeroResizeDebounceTimer != null) {
          clearTimeout(this.#stepperHeroResizeDebounceTimer);
          this.#stepperHeroResizeDebounceTimer = null;
        }
        if (this.#stepperStickyLayoutRaf != null) {
          cancelAnimationFrame(this.#stepperStickyLayoutRaf);
          this.#stepperStickyLayoutRaf = null;
        }
        if (this.#stepperStickyArmRaf != null) {
          cancelAnimationFrame(this.#stepperStickyArmRaf);
          this.#stepperStickyArmRaf = null;
        }
        if (this.#stepperStickyArmOnTimer != null) {
          clearTimeout(this.#stepperStickyArmOnTimer);
          this.#stepperStickyArmOnTimer = null;
        }
        if (this.#stepperStickySlideInPrepTimer != null) {
          clearTimeout(this.#stepperStickySlideInPrepTimer);
          this.#stepperStickySlideInPrepTimer = null;
        }
        if (this.#stepperStickyMotionEnterFallbackTimer != null) {
          clearTimeout(this.#stepperStickyMotionEnterFallbackTimer);
          this.#stepperStickyMotionEnterFallbackTimer = null;
        }
        if (this.#stepperStickyArmOffTimer != null) {
          clearTimeout(this.#stepperStickyArmOffTimer);
          this.#stepperStickyArmOffTimer = null;
        }
        if (this.#stepperHeroMinHeightEaseTimer != null) {
          clearTimeout(this.#stepperHeroMinHeightEaseTimer);
          this.#stepperHeroMinHeightEaseTimer = null;
        }
        if (this.#stepperStickyUnstickExpandTimer != null) {
          clearTimeout(this.#stepperStickyUnstickExpandTimer);
          this.#stepperStickyUnstickExpandTimer = null;
        }
        if (typeof window !== "undefined") {
          if (this.#stepperStickyOnScrollBound) {
            window.removeEventListener("scroll", this.#stepperStickyOnScrollBound);
          }
          if (this.#stepperStickyOnWinResizeBound) {
            window.removeEventListener("resize", this.#stepperStickyOnWinResizeBound);
          }
        }

        this.#stickyLayoutCacheKey = "";

        const root = this.#getCbbSectionDomRoot();
        if (root instanceof HTMLElement) {
          root.style.removeProperty("--cbb-stepper-sticky-stack");
          root.style.removeProperty("--cbb-bundle-sticky-clearance");
          root.style.removeProperty("--cbb-intro-sticky-below-stepper-gap");
          root.style.removeProperty("--cbb-intro-sticky-below-chrome-gap");
        }

        const hero = this.querySelector("[data-cbb-stepper-hero]");
        if (hero instanceof HTMLElement) {
          this.#abortStepperStickyLiveMotion(hero);
          this.#abortStepperStickyUnstickExpand(hero);
          this.#clearStepperHeroFlowLock(hero);
          hero.classList.remove(
            "custom-bundle-builder-v2__stepper-hero--sticky-live",
            "custom-bundle-builder-v2__stepper-hero--sticky-live-slide-in-prep",
            "custom-bundle-builder-v2__stepper-hero--sticky-live-motion-enter",
            "custom-bundle-builder-v2__stepper-hero--sticky-unstick-prep"
          );
          hero.removeAttribute("inert");
          hero.querySelectorAll(".custom-bundle-builder-v2__stepper-copy").forEach((el) => {
            if (el instanceof HTMLElement) el.removeAttribute("aria-hidden");
          });
        }
      }

      #getCbbSectionDomRoot() {
        const p = this.parentElement;
        return p instanceof HTMLElement && p.classList.contains("section-custom-bundle-builder-v2-root") ? p : null;
      }

      /**
       * Scroll-driven arm (hysteresis + short delay on engage): avoids IntersectionObserver fighting
       * scroll anchoring when the hero compacts and changes document height.
       */
      #scheduleStepperStickyArmFromScroll() {
        if (this.dataset.stepperSticky !== "true") return;
        if (this.#stepperStickyArmRaf != null) return;
        this.#stepperStickyArmRaf = requestAnimationFrame(() => {
          this.#stepperStickyArmRaf = null;
          this.#syncStepperStickyArmFromScroll();
        });
      }

      /** @param {HTMLElement} hero */
      #abortStepperStickyLiveMotion(hero) {
        this.#cancelStepperHeroMinHeightEase();
        if (this.#stepperStickySlideInPrepTimer != null) {
          clearTimeout(this.#stepperStickySlideInPrepTimer);
          this.#stepperStickySlideInPrepTimer = null;
        }
        if (this.#stepperStickyMotionEnterFallbackTimer != null) {
          clearTimeout(this.#stepperStickyMotionEnterFallbackTimer);
          this.#stepperStickyMotionEnterFallbackTimer = null;
        }
        hero.classList.remove(
          "custom-bundle-builder-v2__stepper-hero--sticky-live-slide-in-prep",
          "custom-bundle-builder-v2__stepper-hero--sticky-live-motion-reset",
          "custom-bundle-builder-v2__stepper-hero--sticky-live-motion-enter"
        );
      }

      /** @param {HTMLElement} hero @param {boolean} hide */
      #setStepperCopyAriaHidden(hero, hide) {
        hero.querySelectorAll(".custom-bundle-builder-v2__stepper-copy, .custom-bundle-builder-v2__stepper-banner-title, .custom-bundle-builder-v2__stepper-banner-note").forEach((el) => {
          if (!(el instanceof HTMLElement)) return;
          if (hide) el.setAttribute("aria-hidden", "true");
          else el.removeAttribute("aria-hidden");
        });
      }

      /** @returns {boolean} Narrow layout, stepper pinned, intro accordion expanded (sticky bar is visually hidden). */
      #introAccordionHidesStickyStepper() {
        if (this.dataset.stepperSticky !== "true") return false;
        const hero = this.querySelector("[data-cbb-stepper-hero]");
        if (!(hero instanceof HTMLElement)) return false;
        if (!hero.classList.contains("custom-bundle-builder-v2__stepper-hero--sticky-live")) return false;
        if (hero.classList.contains("custom-bundle-builder-v2__stepper-hero--sticky-live-slide-in-prep")) return false;
        if (!this.#isNarrowBundleLayout()) return false;
        const wrap = this.querySelector("[data-cbb-intro-accordion]");
        return (
          wrap instanceof HTMLElement &&
          wrap.classList.contains("custom-bundle-builder-v2__intro-actions-accordion--open")
        );
      }

      #syncStepperHeroInertForIntroAccordion() {
        if (this.dataset.stepperSticky !== "true") return;
        const hero = this.querySelector("[data-cbb-stepper-hero]");
        if (!(hero instanceof HTMLElement)) return;
        if (this.#introAccordionHidesStickyStepper()) {
          hero.setAttribute("inert", "");
        } else {
          hero.removeAttribute("inert");
        }
      }

      #prefersReducedMotion() {
        return (
          typeof window !== "undefined" &&
          typeof window.matchMedia === "function" &&
          window.matchMedia("(prefers-reduced-motion: reduce)").matches
        );
      }

      #cancelStepperHeroMinHeightEase() {
        if (this.#stepperHeroMinHeightEaseTimer != null) {
          clearTimeout(this.#stepperHeroMinHeightEaseTimer);
          this.#stepperHeroMinHeightEaseTimer = null;
        }
        if (this.#stepperHeroMinHeightEaseListenerCleanup != null) {
          this.#stepperHeroMinHeightEaseListenerCleanup();
          this.#stepperHeroMinHeightEaseListenerCleanup = null;
        }
      }

      /** Clears temporary in-flow `min-height` lock (disarm / disconnect). */
      #clearStepperHeroFlowLock(hero) {
        this.#cancelStepperHeroMinHeightEase();
        if (!(hero instanceof HTMLElement)) return;
        hero.style.removeProperty("transition");
        hero.style.removeProperty("min-height");
      }

      /**
       * Ease hero `min-height` toward the compact motion layer — run in parallel with `.stepper-hero__motion` slide-in
       * so flow below tracks the bar visually (timing matches `CBB_STEPPER_STICKY.minHeightEaseS`).
       * @param {HTMLElement} hero
       * @param {HTMLElement | null} motionEl
       */
      #easeStepperHeroFlowLock(hero, motionEl) {
        this.#cancelStepperHeroMinHeightEase();
        if (!(hero instanceof HTMLElement)) return;
        if (!(motionEl instanceof HTMLElement)) {
          this.#clearStepperHeroFlowLock(hero);
          this.#scheduleStepperStickyLayout();
          return;
        }
        const targetPx = Math.max(1, Math.round(motionEl.offsetHeight));
        const raw = hero.style.minHeight;
        const locked = raw ? parseFloat(raw) : NaN;
        if (!Number.isFinite(locked) || locked <= targetPx + 1) {
          this.#clearStepperHeroFlowLock(hero);
          this.#scheduleStepperStickyLayout();
          return;
        }
        if (this.#prefersReducedMotion()) {
          hero.style.minHeight = `${targetPx}px`;
          requestAnimationFrame(() => {
            this.#clearStepperHeroFlowLock(hero);
            this.#scheduleStepperStickyLayout();
          });
          return;
        }
        let finished = false;
        const finish = () => {
          if (finished) return;
          finished = true;
          if (this.#stepperHeroMinHeightEaseTimer != null) {
            clearTimeout(this.#stepperHeroMinHeightEaseTimer);
            this.#stepperHeroMinHeightEaseTimer = null;
          }
          if (this.#stepperHeroMinHeightEaseListenerCleanup != null) {
            this.#stepperHeroMinHeightEaseListenerCleanup();
            this.#stepperHeroMinHeightEaseListenerCleanup = null;
          }
          hero.style.removeProperty("transition");
          hero.style.removeProperty("min-height");
          this.#scheduleStepperStickyLayout();
        };
        const onEnd = (ev) => {
          if (ev.target !== hero || ev.propertyName !== "min-height") return;
          finish();
        };
        hero.addEventListener("transitionend", onEnd);
        this.#stepperHeroMinHeightEaseListenerCleanup = () => {
          hero.removeEventListener("transitionend", onEnd);
        };
        hero.style.transition = `min-height ${CBB_STEPPER_STICKY.minHeightEaseS}s cubic-bezier(${CBB_STEPPER_STICKY.minHeightEaseCssBezier})`;
        requestAnimationFrame(() => {
          hero.style.minHeight = `${targetPx}px`;
        });
        this.#stepperHeroMinHeightEaseTimer = window.setTimeout(finish, CBB_STEPPER_STICKY.minHeightEaseFallbackMs);
      }

      /** @param {HTMLElement} hero */
      #abortStepperStickyUnstickExpand(hero) {
        if (this.#stepperStickyUnstickExpandTimer != null) {
          clearTimeout(this.#stepperStickyUnstickExpandTimer);
          this.#stepperStickyUnstickExpandTimer = null;
        }
        if (hero instanceof HTMLElement) {
          hero.classList.remove("custom-bundle-builder-v2__stepper-hero--sticky-unstick-prep");
        }
      }

      #finishStepperStickyUnstickExpand() {
        this.#stepperStickyUnstickExpandTimer = null;
        if (!this.isConnected || this.dataset.stepperSticky !== "true") return;
        const hero = this.querySelector("[data-cbb-stepper-hero]");
        if (!(hero instanceof HTMLElement)) return;
        if (!hero.classList.contains("custom-bundle-builder-v2__stepper-hero--sticky-unstick-prep")) return;
        hero.classList.remove("custom-bundle-builder-v2__stepper-hero--sticky-unstick-prep");
        this.#setStepperCopyAriaHidden(hero, false);
        this.#scheduleStepperStickyLayout();
        this.#syncStepperHeroInertForIntroAccordion();
      }

      /** @param {boolean} wantLive */
      #applyStepperStickyLive(wantLive) {
        const hero = this.querySelector("[data-cbb-stepper-hero]");
        if (!(hero instanceof HTMLElement)) return;

        const live = hero.classList.contains("custom-bundle-builder-v2__stepper-hero--sticky-live");
        if (live === wantLive) {
          return;
        }

        this.#stickyLayoutCacheKey = "";
        if (this.#stepperStickyArmOffTimer != null) {
          clearTimeout(this.#stepperStickyArmOffTimer);
          this.#stepperStickyArmOffTimer = null;
        }

        const reduced = this.#prefersReducedMotion();
        const motion =
          hero.querySelector("[data-cbb-stepper-motion]") || hero.querySelector(".custom-bundle-builder-v2__stepper");

        if (wantLive) {
          this.#abortStepperStickyLiveMotion(hero);
          this.#abortStepperStickyUnstickExpand(hero);
          if (live) {
            this.#scheduleStepperStickyLayout();
            return;
          }

          /** Extra hold after rAF so compact prep paints off-screen before slide-in (reduces jump / flash). */
          const slideInPrepHoldMs = CBB_STEPPER_STICKY.slideInPrepHoldMs;

          /* Lock flow to expanded height, then `#easeStepperHeroFlowLock` shrinks toward the compact bar so content below moves gradually. */
          const flowLockPx = Math.max(0, Math.round(hero.getBoundingClientRect().height));
          if (flowLockPx > 0) {
            hero.style.minHeight = `${flowLockPx}px`;
          }

          hero.classList.add("custom-bundle-builder-v2__stepper-hero--sticky-live");
          this.#setStepperCopyAriaHidden(hero, true);

          if (reduced || !(motion instanceof HTMLElement)) {
            this.#scheduleStepperStickyLayout();
            this.#syncStepperHeroInertForIntroAccordion();
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                this.#easeStepperHeroFlowLock(hero, motion instanceof HTMLElement ? motion : null);
              });
            });
            return;
          }

          hero.classList.add("custom-bundle-builder-v2__stepper-hero--sticky-live-slide-in-prep");
          this.#scheduleStepperStickyLayout();
          this.#syncStepperHeroInertForIntroAccordion();

          const finishSlideInPrep = () => {
            this.#stepperStickySlideInPrepTimer = null;
            if (!this.isConnected || this.dataset.stepperSticky !== "true") return;
            const h = this.querySelector("[data-cbb-stepper-hero]");
            if (!(h instanceof HTMLElement)) return;
            if (!h.classList.contains("custom-bundle-builder-v2__stepper-hero--sticky-live-slide-in-prep")) return;

            const motionEl = h.querySelector("[data-cbb-stepper-motion]");
            h.classList.remove("custom-bundle-builder-v2__stepper-hero--sticky-live-slide-in-prep");
            h.classList.add("custom-bundle-builder-v2__stepper-hero--sticky-live-motion-enter");
            this.#scheduleStepperStickyLayout();

            if (motionEl instanceof HTMLElement && !this.#prefersReducedMotion()) {
              requestAnimationFrame(() => {
                this.#easeStepperHeroFlowLock(h, motionEl);
              });
              const onMotionEnd = (ev) => {
                if (ev.propertyName !== "transform") return;
                if (this.#stepperStickyMotionEnterFallbackTimer != null) {
                  clearTimeout(this.#stepperStickyMotionEnterFallbackTimer);
                  this.#stepperStickyMotionEnterFallbackTimer = null;
                }
                h.classList.remove("custom-bundle-builder-v2__stepper-hero--sticky-live-motion-enter");
                motionEl.removeEventListener("transitionend", onMotionEnd);
              };
              motionEl.addEventListener("transitionend", onMotionEnd);
              this.#stepperStickyMotionEnterFallbackTimer = window.setTimeout(() => {
                this.#stepperStickyMotionEnterFallbackTimer = null;
                h.classList.remove("custom-bundle-builder-v2__stepper-hero--sticky-live-motion-enter");
                motionEl.removeEventListener("transitionend", onMotionEnd);
              }, CBB_STEPPER_STICKY.motionEnterFallbackMs);
            } else {
              h.classList.remove("custom-bundle-builder-v2__stepper-hero--sticky-live-motion-enter");
              this.#easeStepperHeroFlowLock(
                h,
                motionEl instanceof HTMLElement ? motionEl : null
              );
            }

            this.#syncStepperHeroInertForIntroAccordion();
          };

          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                if (this.#stepperStickySlideInPrepTimer != null) {
                  clearTimeout(this.#stepperStickySlideInPrepTimer);
                }
                this.#stepperStickySlideInPrepTimer = setTimeout(finishSlideInPrep, slideInPrepHoldMs);
              });
            });
          });
          return;
        }

        if (!live) return;

        this.#abortStepperStickyLiveMotion(hero);
        this.#abortStepperStickyUnstickExpand(hero);
        hero.classList.add("custom-bundle-builder-v2__stepper-hero--sticky-unstick-prep");

        this.#clearStepperHeroFlowLock(hero);
        hero.classList.remove(
          "custom-bundle-builder-v2__stepper-hero--sticky-live",
          "custom-bundle-builder-v2__stepper-hero--sticky-live-motion-enter"
        );
        this.#setStepperCopyAriaHidden(hero, true);
        this.#scheduleStepperStickyLayout();
        this.#syncStepperHeroInertForIntroAccordion();

        const reducedUnstick = this.#prefersReducedMotion();
        const unstickHoldMs = CBB_STEPPER_STICKY.unstickHoldMs;

        if (reducedUnstick) {
          hero.classList.remove("custom-bundle-builder-v2__stepper-hero--sticky-unstick-prep");
          this.#setStepperCopyAriaHidden(hero, false);
          this.#scheduleStepperStickyLayout();
          this.#syncStepperHeroInertForIntroAccordion();
          return;
        }

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (this.#stepperStickyUnstickExpandTimer != null) {
              clearTimeout(this.#stepperStickyUnstickExpandTimer);
            }
            this.#stepperStickyUnstickExpandTimer = window.setTimeout(() => {
              this.#finishStepperStickyUnstickExpand();
            }, unstickHoldMs);
          });
        });
      }

      #syncStepperStickyArmFromScroll() {
        if (this.dataset.stepperSticky !== "true") return;
        const hero = this.querySelector("[data-cbb-stepper-hero]");
        const armSentinel = this.querySelector("[data-cbb-stepper-arm-sentinel]");
        if (!(hero instanceof HTMLElement) || !(armSentinel instanceof HTMLElement)) return;

        const armEnterPx = CBB_STEPPER_STICKY.armEnterPx;
        const armExtraScrollPx = CBB_STEPPER_STICKY.armExtraScrollPx;
        const armReleasePx = CBB_STEPPER_STICKY.armReleasePx;
        const armOnDelayMs = CBB_STEPPER_STICKY.armOnDelayMs;
        const armOffDelayMs = CBB_STEPPER_STICKY.armOffDelayMs;
        const stickyStackTop = cbbReadThemeHeaderStackPx();
        const armEnterLine = stickyStackTop + armEnterPx - armExtraScrollPx;
        const armReleaseLine = stickyStackTop + armReleasePx;

        const top = armSentinel.getBoundingClientRect().top;
        const live = hero.classList.contains("custom-bundle-builder-v2__stepper-hero--sticky-live");

        if (!live) {
          if (this.#stepperStickyArmOffTimer != null) {
            clearTimeout(this.#stepperStickyArmOffTimer);
            this.#stepperStickyArmOffTimer = null;
          }
          if (top > armEnterLine) {
            if (this.#stepperStickyArmOnTimer != null) {
              clearTimeout(this.#stepperStickyArmOnTimer);
              this.#stepperStickyArmOnTimer = null;
            }
            return;
          }
          if (this.#stepperStickyArmOnTimer != null) return;

          this.#stepperStickyArmOnTimer = setTimeout(() => {
            this.#stepperStickyArmOnTimer = null;
            if (this.dataset.stepperSticky !== "true") return;
            const h = this.querySelector("[data-cbb-stepper-hero]");
            const s = this.querySelector("[data-cbb-stepper-arm-sentinel]");
            if (!(h instanceof HTMLElement) || !(s instanceof HTMLElement)) return;
            if (h.classList.contains("custom-bundle-builder-v2__stepper-hero--sticky-live")) return;
            const stack = cbbReadThemeHeaderStackPx();
            const enterLine = stack + armEnterPx - armExtraScrollPx;
            const t = s.getBoundingClientRect().top;
            if (t <= enterLine) {
              requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                  if (this.dataset.stepperSticky !== "true") return;
                  const h2 = this.querySelector("[data-cbb-stepper-hero]");
                  const s2 = this.querySelector("[data-cbb-stepper-arm-sentinel]");
                  if (!(h2 instanceof HTMLElement) || !(s2 instanceof HTMLElement)) return;
                  if (h2.classList.contains("custom-bundle-builder-v2__stepper-hero--sticky-live")) return;
                  const stack2 = cbbReadThemeHeaderStackPx();
                  if (s2.getBoundingClientRect().top > stack2 + armEnterPx - armExtraScrollPx) return;
                  this.#applyStepperStickyLive(true);
                });
              });
            }
          }, armOnDelayMs);
          return;
        }

        if (this.#stepperStickyArmOnTimer != null) {
          clearTimeout(this.#stepperStickyArmOnTimer);
          this.#stepperStickyArmOnTimer = null;
        }

        if (top <= armReleaseLine) {
          if (this.#stepperStickyArmOffTimer != null) {
            clearTimeout(this.#stepperStickyArmOffTimer);
            this.#stepperStickyArmOffTimer = null;
          }
          return;
        }

        if (this.#stepperStickyArmOffTimer != null) return;

        this.#stepperStickyArmOffTimer = window.setTimeout(() => {
          this.#stepperStickyArmOffTimer = null;
          if (this.dataset.stepperSticky !== "true") return;
          const h = this.querySelector("[data-cbb-stepper-hero]");
          const s = this.querySelector("[data-cbb-stepper-arm-sentinel]");
          if (!(h instanceof HTMLElement) || !(s instanceof HTMLElement)) return;
          if (!h.classList.contains("custom-bundle-builder-v2__stepper-hero--sticky-live")) return;
          const stackOff = cbbReadThemeHeaderStackPx();
          if (s.getBoundingClientRect().top > stackOff + armReleasePx) {
            this.#applyStepperStickyLive(false);
          }
        }, armOffDelayMs);
      }

      /** Coalesces DOM reads to one frame (ResizeObserver + observers + resize). */
      #scheduleStepperStickyLayout() {
        if (this.dataset.stepperSticky !== "true") return;
        if (this.#stepperStickyLayoutRaf != null) return;
        this.#stepperStickyLayoutRaf = requestAnimationFrame(() => {
          this.#stepperStickyLayoutRaf = null;
          this.#syncStepperStickyLayout();
        });
      }

      #syncStepperStickyLayout() {
        if (this.dataset.stepperSticky !== "true") return;
        const root = this.#getCbbSectionDomRoot();
        const hero = this.querySelector("[data-cbb-stepper-hero]");
        if (!(root instanceof HTMLElement) || !(hero instanceof HTMLElement)) return;

        const live = hero.classList.contains("custom-bundle-builder-v2__stepper-hero--sticky-live");
        if (!live) {
          if (this.#stickyLayoutCacheKey === "0") return;
          this.#stickyLayoutCacheKey = "0";
          root.style.setProperty("--cbb-stepper-sticky-stack", "0px");
          root.style.setProperty("--cbb-bundle-sticky-clearance", "0px");
          root.style.setProperty("--cbb-intro-sticky-below-stepper-gap", "0px");
          root.style.removeProperty("--cbb-intro-sticky-below-chrome-gap");
          this.#scheduleIntroActionsSlotStuckUpdate();
          return;
        }

        if (this.#introAccordionHidesStickyStepper()) {
          const hs = cbbReadThemeHeaderStackPx();
          const key = `a:${Math.round(hs)}`;
          if (this.#stickyLayoutCacheKey === key) return;
          this.#stickyLayoutCacheKey = key;
          root.style.setProperty("--cbb-stepper-sticky-stack", "0px");
          root.style.setProperty("--cbb-intro-sticky-below-stepper-gap", "0px");
          root.style.removeProperty("--cbb-intro-sticky-below-chrome-gap");
          root.style.setProperty("--cbb-bundle-sticky-clearance", `${Math.round(hs)}px`);
          this.#scheduleIntroActionsSlotStuckUpdate();
          return;
        }

        const hs = cbbReadThemeHeaderStackPx();
        const motionEl =
          hero.querySelector("[data-cbb-stepper-motion]") || hero.querySelector(".custom-bundle-builder-v2__stepper");
        const hh =
          motionEl instanceof HTMLElement
            ? Math.max(0, motionEl.offsetHeight)
            : Math.max(0, Math.ceil(hero.getBoundingClientRect().height));
        const introGapPx = CBB_STEPPER_STICKY.introGapPx;
        const key = `l:${Math.round(hs)}:${Math.round(hh)}:${introGapPx}`;
        if (this.#stickyLayoutCacheKey === key) return;
        this.#stickyLayoutCacheKey = key;
        root.style.setProperty("--cbb-stepper-sticky-stack", `${hh}px`);
        root.style.setProperty("--cbb-intro-sticky-below-stepper-gap", `${introGapPx}px`);
        root.style.setProperty("--cbb-intro-sticky-below-chrome-gap", "0px");
        root.style.setProperty("--cbb-bundle-sticky-clearance", `${Math.round(hs + hh + introGapPx)}px`);
        this.#scheduleIntroActionsSlotStuckUpdate();
      }

      /** @param {HTMLElement} slot */
      #computeIntroActionsSlotStuckMaxHeightPx(slot) {
        const bottomInsetRaw = parseFloat(getComputedStyle(slot).bottom);
        const bottomInset = Number.isFinite(bottomInsetRaw) ? bottomInsetRaw : 0;
        const vp = cbbReadViewportBox();
        const slotRect = slot.getBoundingClientRect();

        let ceiling = Math.max(vp.offsetTop, cbbReadVisibleThemeHeaderStackPx());

        // Reserve in-flow stepper at page top too — `--sticky-live` arms only after extra scroll.
        if (!this.#introAccordionHidesStickyStepper()) {
          const hero = this.querySelector("[data-cbb-stepper-hero]");
          if (hero instanceof HTMLElement) {
            const heroRect = hero.getBoundingClientRect();
            const inTopBand = heroRect.bottom > ceiling && heroRect.top <= ceiling + 24;
            const stickyLive =
              this.dataset.stepperSticky === "true" &&
              hero.classList.contains("custom-bundle-builder-v2__stepper-hero--sticky-live");
            if (inTopBand || stickyLive) {
              ceiling = Math.max(
                ceiling,
                Math.ceil(heroRect.bottom) + CBB_STEPPER_STICKY.introGapPx
              );
            }
          }
        }

        slot.style.setProperty("--cbb-intro-actions-slot-urlbar-overlap", `${vp.urlBarOverlap}px`);

        const pinBottom = Math.min(slotRect.bottom, vp.bottom - bottomInset);
        return Math.max(120, Math.round(pinBottom - ceiling - 8));
      }

      /** Swiper carousel for step product cards; disabled via section setting (static grid in markup). */
      #swiperEnabled() {
        return this.dataset.enableSwiper === "true";
      }

      /** Next/Previous advance by slidesPerView columns (Swiper slidesPerGroup). */
      #swiperNavGroupByView() {
        return String(this.dataset.swiperNavGroupByView || "") === "true";
      }

      /** @param {boolean} [disconnectCleanup] When disconnecting CE, restore CTA markup into intro column unconditionally. */
      #dockIntroActionsUnderSummary(disconnectCleanup = false) {
        const introCol = this.querySelector(".custom-bundle-builder-v2__column--intro");
        const slot = this.querySelector("[data-cbb-intro-actions-slot]");
        if (!(introCol instanceof HTMLElement) || !(slot instanceof HTMLElement)) return;

        const actionsEl =
          introCol.querySelector(".custom-bundle-builder-v2__intro-actions") || slot.querySelector(".custom-bundle-builder-v2__intro-actions");

        if (!(actionsEl instanceof HTMLElement)) return;

        if (disconnectCleanup) {
          if (actionsEl.parentElement !== introCol) introCol.append(actionsEl);
          this.#scheduleIntroActionsSlotStuckUpdate();
          return;
        }

        if (this.#isNarrowBundleLayout()) {
          if (actionsEl.parentElement !== slot) slot.append(actionsEl);
          this.#scheduleIntroActionsSlotStuckUpdate();
          return;
        }

        if (actionsEl.parentElement !== introCol) introCol.append(actionsEl);
        this.#scheduleIntroActionsSlotStuckUpdate();
      }

      #bindIntroActionsSlotStuckTracking() {
        if (typeof window === "undefined" || !this.#onWindowScrollOrResizeBound) return;
        window.addEventListener("scroll", this.#onWindowScrollOrResizeBound, { passive: true });
        window.addEventListener("resize", this.#onWindowScrollOrResizeBound);
        if (window.visualViewport) {
          window.visualViewport.addEventListener("resize", this.#onWindowScrollOrResizeBound);
          window.visualViewport.addEventListener("scroll", this.#onWindowScrollOrResizeBound);
        }
        this.#scheduleIntroActionsSlotStuckUpdate();
      }

      #unbindIntroActionsSlotStuckTracking() {
        if (typeof window !== "undefined" && this.#onWindowScrollOrResizeBound) {
          window.removeEventListener("scroll", this.#onWindowScrollOrResizeBound);
          window.removeEventListener("resize", this.#onWindowScrollOrResizeBound);
          if (window.visualViewport) {
            window.visualViewport.removeEventListener("resize", this.#onWindowScrollOrResizeBound);
            window.visualViewport.removeEventListener("scroll", this.#onWindowScrollOrResizeBound);
          }
        }
        if (this.#introSlotStuckRaf != null) {
          cancelAnimationFrame(this.#introSlotStuckRaf);
          this.#introSlotStuckRaf = null;
        }
        const slot = this.querySelector("[data-cbb-intro-actions-slot]");
        if (slot instanceof HTMLElement) {
          slot.classList.remove("custom-bundle-builder-v2__intro-actions-slot--stuck");
          slot.style.removeProperty("--cbb-intro-actions-slot-stuck-max-height");
          slot.style.removeProperty("--cbb-intro-actions-slot-urlbar-overlap");
        }
      }

      #scheduleIntroActionsSlotStuckUpdate() {
        if (this.#introSlotStuckRaf != null) return;
        this.#introSlotStuckRaf = requestAnimationFrame(() => {
          this.#introSlotStuckRaf = null;
          this.#syncPrimarySummaryViewportOverlap();
          this.#syncIntroActionsSlotStuckClass();
        });
      }

      /**
       * Mobile: slot uses `position: sticky; bottom`. Toggle BEM modifier when the bar is pinned to the viewport.
       */
      #syncIntroActionsSlotStuckClass() {
        const slot = this.querySelector("[data-cbb-intro-actions-slot]");
        if (!(slot instanceof HTMLElement)) return;

        const STUCK_CLASS = "custom-bundle-builder-v2__intro-actions-slot--stuck";
        const MAX_HEIGHT_VAR = "--cbb-intro-actions-slot-stuck-max-height";

        const clearStuckState = () => {
          slot.classList.remove(STUCK_CLASS);
          slot.style.removeProperty(MAX_HEIGHT_VAR);
          slot.style.removeProperty("--cbb-intro-actions-slot-urlbar-overlap");
        };

        if (!this.#isNarrowBundleLayout()) {
          clearStuckState();
          return;
        }

        if (!slot.querySelector(".custom-bundle-builder-v2__intro-actions")) {
          clearStuckState();
          return;
        }

        const pos = getComputedStyle(slot).position;
        if (pos !== "sticky") {
          clearStuckState();
          return;
        }

        const rect = slot.getBoundingClientRect();
        const bottomInset = parseFloat(getComputedStyle(slot).bottom);
        const insetPx = Number.isFinite(bottomInset) ? bottomInset : 0;
        const vp = cbbReadViewportBox();
        const layoutPinLine = vp.layoutH - insetPx;
        const visualPinLine = vp.bottom - insetPx;
        const pinLine = Math.min(layoutPinLine, visualPinLine);

        const containing = slot.parentElement;
        const containingBottom =
          containing instanceof HTMLElement ? containing.getBoundingClientRect().bottom : rect.bottom;
        // CSS sticky-bottom stays pinned while the containing block still extends past the pin line.
        // Using only `rect.bottom` at section end fights `max-height` (open list grows/shrinks → jump).
        const runway = containingBottom - pinLine;
        const wasStuck = slot.classList.contains(STUCK_CLASS);
        const stuck = rect.top < vp.bottom && runway > 8 && rect.bottom >= pinLine - 8;

        const accordion = this.querySelector("[data-cbb-intro-accordion]");
        const accordionOpen =
          accordion instanceof HTMLElement &&
          accordion.classList.contains("custom-bundle-builder-v2__intro-actions-accordion--open");

        if (wasStuck && !stuck && accordionOpen) {
          this.#resetIntroAccordionPanel();
          clearStuckState();
          return;
        }

        if (stuck) {
          slot.classList.add(STUCK_CLASS);
          const nextMax = this.#computeIntroActionsSlotStuckMaxHeightPx(slot);
          const prevMax = parseFloat(slot.style.getPropertyValue(MAX_HEIGHT_VAR));
          if (!Number.isFinite(prevMax) || Math.abs(prevMax - nextMax) >= 2) {
            slot.style.setProperty(MAX_HEIGHT_VAR, `${nextMax}px`);
          }
        } else {
          clearStuckState();
        }
      }

      /** @param {Element | null | undefined} panel */
      #dockSwiperPaginationInPanel(panel) {
        if (!(panel instanceof Element)) return;
        const paginationEl = panel.querySelector("[data-cbb-swiper-pagination]");
        const desktopHost = panel.querySelector("[data-cbb-swiper-pagination-host-desktop]");
        const mobileHost = panel.querySelector("[data-cbb-swiper-pagination-host-mobile]");
        if (!(paginationEl instanceof HTMLElement)) return;

        const target =
          desktopHost instanceof HTMLElement && mobileHost instanceof HTMLElement
            ? this.#isNarrowBundleLayout()
              ? mobileHost
              : desktopHost
            : desktopHost instanceof HTMLElement
              ? desktopHost
              : mobileHost;

        if (!(target instanceof HTMLElement)) return;
        target.appendChild(paginationEl);

        if (desktopHost instanceof HTMLElement && mobileHost instanceof HTMLElement) {
          const narrow = this.#isNarrowBundleLayout();

          desktopHost.hidden = narrow;
          desktopHost.setAttribute("aria-hidden", narrow ? "true" : "false");

          mobileHost.hidden = !narrow;
          mobileHost.setAttribute("aria-hidden", narrow ? "false" : "true");

          if (narrow) {
            const stepRaw = panel.getAttribute("data-cbb-step-panel");
            const stepIndex = parseInt(stepRaw || "0", 10);
            const sw = Number.isFinite(stepIndex) ? this.#swipers.get(stepIndex) : undefined;
            if (sw) {
              this.#syncCbbSwiperPagination(sw);
            } else {
              mobileHost.hidden = true;
              mobileHost.setAttribute("aria-hidden", "true");
            }
          }
        }
      }

      #biscuitsBridgeActive() {
        return String(this.dataset.biscuitsBridge || "").toLowerCase() === "true";
      }

      #qtySelectorAlwaysVisible() {
        return String(this.dataset.qtyAlwaysVisible || "").toLowerCase() === "true";
      }

      /** Document capture: close intro accordion on outside tap (mobile narrow). Runs before toggle handlers. */
      #onIntroAccordionOutsidePointerDown(e) {
        if (e.pointerType === "mouse" && e.button !== 0) return;
        if (!(e.target instanceof Element)) return;
        if (!this.#isNarrowBundleLayout()) return;

        const wrap = this.querySelector("[data-cbb-intro-accordion]");
        if (!(wrap instanceof HTMLElement)) return;
        if (!wrap.classList.contains("custom-bundle-builder-v2__intro-actions-accordion--open")) return;

        const target = e.target;
        const introCard = this.querySelector(".custom-bundle-builder-v2__intro-card");
        // Toolbar remove-all / CTAs sit outside `[data-cbb-intro-accordion]` but still belong to the intro card.
        if (introCard instanceof HTMLElement && introCard.contains(target)) return;

        this.#resetIntroAccordionPanel();
      }

      /** Page scroll (not inner slot / visualViewport) closes the expanded sticky list. */
      #collapseIntroAccordionOnPageScroll() {
        if (!this.#isNarrowBundleLayout()) return;

        const wrap = this.querySelector("[data-cbb-intro-accordion]");
        if (!(wrap instanceof HTMLElement)) return;
        if (!wrap.classList.contains("custom-bundle-builder-v2__intro-actions-accordion--open")) return;

        const y = window.scrollY || window.pageYOffset || 0;

        if (Date.now() < this.#introAccordionIgnorePageScrollUntil) {
          this.#introAccordionOpenScrollY = y;
          return;
        }

        if (Math.abs(y - this.#introAccordionOpenScrollY) < CBB_INTRO_ACCORDION_PAGE_SCROLL_PX) return;

        this.#resetIntroAccordionPanel();
      }

      /** Collapses the mobile intro accordion panel (breakpoint / Theme Editor safe). */
      #resetIntroAccordionPanel() {
        const wrap = this.querySelector("[data-cbb-intro-accordion]");
        if (!(wrap instanceof HTMLElement) || !this.#isNarrowBundleLayout()) return;

        wrap.classList.remove("custom-bundle-builder-v2__intro-actions-accordion--open");

        const panel = wrap.querySelector("[data-cbb-intro-accordion-panel]");

        // Toolbar toggle sits outside `[data-cbb-intro-accordion]`.
        this.querySelectorAll("[data-cbb-intro-accordion-toggle]").forEach((b) => {
          if (b instanceof HTMLButtonElement) {
            b.setAttribute("aria-expanded", "false");
          }
        });
        if (panel instanceof HTMLElement) {
          panel.setAttribute("aria-hidden", "true");
        }

        this.#scheduleIntroActionsSlotStuckUpdate();
        this.#scheduleStepperStickyLayout();
        this.#syncStepperHeroInertForIntroAccordion();
      }

      /** Desktop: accordion always expanded; mobile: collapsed until user opens. */
      #syncIntroAccordionLayout() {
        const wrap = this.querySelector("[data-cbb-intro-accordion]");
        if (!(wrap instanceof HTMLElement)) return;

        const panel = wrap.querySelector("[data-cbb-intro-accordion-panel]");
        const toggles = wrap.querySelectorAll("[data-cbb-intro-accordion-toggle]");

        if (!this.#isNarrowBundleLayout()) {
          wrap.classList.add("custom-bundle-builder-v2__intro-actions-accordion--open");
          toggles.forEach((b) => {
            if (b instanceof HTMLButtonElement) {
              b.setAttribute("aria-expanded", "true");
            }
          });
          if (panel instanceof HTMLElement) {
            panel.setAttribute("aria-hidden", "false");
          }
          this.#scheduleStepperStickyLayout();
          this.#syncStepperHeroInertForIntroAccordion();
          return;
        }

        if (!wrap.classList.contains("custom-bundle-builder-v2__intro-actions-accordion--open")) {
          toggles.forEach((b) => {
            if (b instanceof HTMLButtonElement) {
              b.setAttribute("aria-expanded", "false");
            }
          });
          if (panel instanceof HTMLElement) {
            panel.setAttribute("aria-hidden", "true");
          }
        }

        this.#scheduleStepperStickyLayout();
        this.#syncStepperHeroInertForIntroAccordion();
      }

      /** @param {HTMLButtonElement} btn */
      #toggleIntroAccordion(btn) {
        if (!this.#isNarrowBundleLayout()) return;

        /** Toolbar toggle lives outside `[data-cbb-intro-accordion]` (sibling above it). */
        let wrap = btn.closest("[data-cbb-intro-accordion]");
        if (!(wrap instanceof HTMLElement)) {
          wrap = this.querySelector("[data-cbb-intro-accordion]");
        }
        if (!(wrap instanceof HTMLElement)) return;

        const panel = wrap.querySelector("[data-cbb-intro-accordion-panel]");

        const willOpen = !wrap.classList.contains("custom-bundle-builder-v2__intro-actions-accordion--open");
        wrap.classList.toggle("custom-bundle-builder-v2__intro-actions-accordion--open", willOpen);

        if (willOpen) {
          this.#introAccordionOpenScrollY = window.scrollY || window.pageYOffset || 0;
          this.#introAccordionIgnorePageScrollUntil = Date.now() + 180;
          requestAnimationFrame(() => {
            this.#introAccordionOpenScrollY = window.scrollY || window.pageYOffset || 0;
          });
        }

        this.querySelectorAll("[data-cbb-intro-accordion-toggle]").forEach((b) => {
          if (b instanceof HTMLButtonElement) {
            b.setAttribute("aria-expanded", willOpen ? "true" : "false");
          }
        });

        if (panel instanceof HTMLElement) {
          panel.setAttribute("aria-hidden", willOpen ? "false" : "true");
        }

        this.#scheduleIntroActionsSlotStuckUpdate();
        this.#scheduleStepperStickyLayout();
        this.#syncStepperHeroInertForIntroAccordion();
      }

      /** Narrow layout: legacy “hide duplicate summary” removed — no-op for compatibility. */
      #syncPrimarySummaryViewportOverlap() {}

      #getSectionRoot() {
        return this.closest(".section-custom-bundle-builder-v2-root");
      }

      #sectionHasBiscuitsStepProducts(root) {
        return Boolean(root && root.querySelector(".biscuits-bundle-step__products"));
      }

      #dismissAppLoader() {
        const loader = this.querySelector("[data-cbb-app-loader]");
        if (loader instanceof HTMLElement) {
          loader.hidden = true;
          loader.setAttribute("aria-hidden", "true");
        }

        const inner = this.querySelector(".custom-bundle-builder-v2__inner");
        if (inner instanceof HTMLElement) {
          inner.style.removeProperty("visibility");
          inner.removeAttribute("data-cbb-biscuits-boot");
        }
      }

      #tryDismissLoaderForBiscuitsReady() {
        const root = this.#getSectionRoot();
        if (!this.#sectionHasBiscuitsStepProducts(root)) return;
        this.#dismissAppLoader();
        document.removeEventListener("BiscuitsBundleForm:ready", this.#onBiscuitsFormReadyBound);
        if (this.#biscuitsMountObserver) {
          this.#biscuitsMountObserver.disconnect();
          this.#biscuitsMountObserver = null;
        }
        this.#syncRestoredSelectionToBiscuitsWhenReady();
      }

      #attachBiscuitsBridge() {
        const root = this.#getSectionRoot();
        if (!root) return;

        this.#tryDismissLoaderForBiscuitsReady();
        if (this.#sectionHasBiscuitsStepProducts(root)) return;

        document.addEventListener("BiscuitsBundleForm:ready", this.#onBiscuitsFormReadyBound);

        this.#biscuitsMountObserver = new MutationObserver(() => {
          this.#tryDismissLoaderForBiscuitsReady();
        });
        this.#biscuitsMountObserver.observe(root, { childList: true, subtree: true });
      }

      /** @param {Event} ev */
      #onBiscuitsFormReady(ev) {
        if (!this.#biscuitsBridgeActive()) return;
        const root = this.#getSectionRoot();
        const t = ev.target;
        if (!(t instanceof Node) || !root || !root.contains(t)) return;
        this.#tryDismissLoaderForBiscuitsReady();
      }

      /** @param {Element} el */
      #closestBiscuitsSelectableHost(el) {
        if (!(el instanceof Element)) return null;
        const variantHost = el.closest("biscuits-bundle-variant");
        if (variantHost instanceof Element) return variantHost;
        const productHost = el.closest("biscuits-bundle-product");
        if (productHost instanceof Element) return productHost;
        const tag = el.tagName.toLowerCase();
        if (tag === "biscuits-bundle-variant" || tag === "biscuits-bundle-product") return el;
        return null;
      }

      /** @param {string} variantId */
      #findBiscuitsProductHostForVariant(variantId) {
        const root = this.#getSectionRoot();
        if (!root) return null;
        const want = String(variantId).trim();

        const candidates = root.querySelectorAll("[data-biscuits-selected-variant-id]");
        for (const el of candidates) {
          if (!(el instanceof Element)) continue;
          if (el.getAttribute("data-biscuits-selected-variant-id") !== want) continue;
          const host = this.#closestBiscuitsSelectableHost(el);
          if (host instanceof Element) return host;
        }

        return null;
      }

      /** @param {HTMLElement} card */
      #bridgeSyncBiscuitsProduct(card) {
        if (!this.#biscuitsBridgeActive()) return;
        const variantId = card.getAttribute("data-variant-id");
        if (!variantId) return;
        const host = this.#findBiscuitsProductHostForVariant(variantId);
        if (!(host instanceof HTMLElement)) return;
        host.click();
      }

      #findCbbThemeAddToCartButton() {
        const root = this.#getSectionRoot();
        if (!root) return null;
        const btn = root.querySelector("#AddToCart");
        return btn instanceof HTMLElement ? btn : null;
      }

      /** @returns {string} */
      #findBiscuitsFormErrorMessage() {
        const root = this.#getSectionRoot();
        if (!root) return "";
        const nodes = root.querySelectorAll(".biscuits-bundle-form__messages--error");
        for (const el of nodes) {
          if (!(el instanceof HTMLElement)) continue;
          const text = (el.textContent || "").replace(/\s+/g, " ").trim();
          if (text.length > 0) return text;
        }
        return "";
      }

      #clearBiscuitsSubmitLoadingUi() {
        if (this.#biscuitsSubmitSafetyTimer != null) {
          clearTimeout(this.#biscuitsSubmitSafetyTimer);
          this.#biscuitsSubmitSafetyTimer = null;
        }
        if (this.#biscuitsSubmitFallbackTimer != null) {
          clearTimeout(this.#biscuitsSubmitFallbackTimer);
          this.#biscuitsSubmitFallbackTimer = null;
        }
        if (this.#biscuitsSubmitAtcObserver) {
          this.#biscuitsSubmitAtcObserver.disconnect();
          this.#biscuitsSubmitAtcObserver = null;
        }

        const cta = this.querySelector("[data-cbb-primary-cta]");
        if (cta instanceof HTMLElement && cta.getAttribute("aria-busy") === "true") {
          cta.removeAttribute("aria-busy");
        }
      }

      #onDeliveryDelayOptionChange(event) {
        const target = event.target;
        if (!(target instanceof HTMLInputElement) || target.type !== "radio") return;
        if (!target.closest("[data-delivery-delay-options]")) return;
        this.#syncDeliveryDelayPropertyToBundleForm();
      }

      #onFreebiesLayoutChanged() {
        this.#scheduleIntroActionsSlotStuckUpdate();
        this.#syncIntroAccordionLayout();
      }

      /** @param {HTMLElement} atc */
      #delegateToBiscuitsAddToCart(atc) {
        this.#clearBiscuitsSubmitLoadingUi();
        this.#syncDeliveryDelayPropertyToBundleForm();

        const cta = this.querySelector("[data-cbb-primary-cta]");

        if (cta instanceof HTMLElement) {
          cta.setAttribute("aria-busy", "true");
        }

        let sawBusy = false;

        this.#biscuitsSubmitFallbackTimer = window.setTimeout(() => {
          if (!sawBusy) {
            this.#clearBiscuitsSubmitLoadingUi();
          }
        }, 1200);

        this.#biscuitsSubmitAtcObserver = new MutationObserver(() => {
          const busy = atc.getAttribute("aria-busy") === "true";

          if (busy) {
            sawBusy = true;
            if (this.#biscuitsSubmitFallbackTimer != null) {
              clearTimeout(this.#biscuitsSubmitFallbackTimer);
              this.#biscuitsSubmitFallbackTimer = null;
            }
            return;
          }

          if (sawBusy) {
            this.#clearBiscuitsSubmitLoadingUi();
          }
        });

        this.#biscuitsSubmitAtcObserver.observe(atc, {
          attributes: true,
          attributeFilter: ["aria-busy", "disabled"],
        });

        this.#biscuitsSubmitSafetyTimer = window.setTimeout(() => {
          this.#clearBiscuitsSubmitLoadingUi();
        }, 60000);

        atc.click();
      }

      #readConfig() {
        const id = this.dataset.sectionId;
        const el = id ? this.querySelector(`#CustomBundleBuilderConfig-${id}`) : this.querySelector("script[id^='CustomBundleBuilderConfig-']");

        if (!el || !el.textContent) return null;

        try {
          const parsed = JSON.parse(el.textContent);
          parsed.discountTiers = [...(parsed.discountTiers || [])].sort((a, b) => (a.threshold || 0) - (b.threshold || 0));
          if (typeof parsed.slotPlaceholderTemplate !== "string" || parsed.slotPlaceholderTemplate.trim() === "") {
            parsed.slotPlaceholderTemplate = "Figurine #__INDEX__";
          }
          if (typeof parsed.slotEmptyImageUrl !== "string") {
            parsed.slotEmptyImageUrl = "";
          }
          const sm = Array.isArray(parsed.stepMinPicks) ? parsed.stepMinPicks.length : 0;
          /** @param {unknown} v */
          const padSteps = (v) => {
            const a = [...(Array.isArray(v) ? v : [])].map((x) => (typeof x === "string" ? x : ""));
            while (a.length < sm) a.push("");
            return sm > 0 ? a.slice(0, sm) : [];
          };
          parsed.stepTierRequiredImageUrls = padSteps(parsed.stepTierRequiredImageUrls);
          parsed.stepTierRequiredLabels = padSteps(parsed.stepTierRequiredLabels);
          parsed.stepLabels = padSteps(parsed.stepLabels);
          parsed.stepTierProgressTemplates = padSteps(parsed.stepTierProgressTemplates);
          const defTierTpl = "Pick __NEED__ more to get __PCT__% off";
          parsed.stepTierProgressTemplates = parsed.stepTierProgressTemplates.map((t) =>
            typeof t === "string" && t.trim() !== "" ? t.trim() : defTierTpl,
          );
          parsed.stepTierProgressTemplatesSingular = padSteps(parsed.stepTierProgressTemplatesSingular);
          parsed.stepTierProgressTemplatesSingular = parsed.stepTierProgressTemplatesSingular.map((t) =>
            typeof t === "string" ? t.trim() : "",
          );
          if (typeof parsed.summaryTotalLabel !== "string") {
            parsed.summaryTotalLabel = "Total";
          }
          if (typeof parsed.summaryYouSaveLabel !== "string") {
            parsed.summaryYouSaveLabel = "You save";
          }
          if (typeof parsed.summaryTitle !== "string") {
            parsed.summaryTitle = "";
          }
          if (typeof parsed.summaryCountTemplate !== "string") {
            parsed.summaryCountTemplate = "";
          }
          parsed.deliveryDelayMap =
            parsed.deliveryDelayMap && typeof parsed.deliveryDelayMap === "object" && !Array.isArray(parsed.deliveryDelayMap)
              ? parsed.deliveryDelayMap
              : {};
          if (typeof parsed.deliveryDelayPropertyName !== "string") {
            parsed.deliveryDelayPropertyName = "";
          } else {
            parsed.deliveryDelayPropertyName = parsed.deliveryDelayPropertyName.trim();
          }
          if (parsed.deliveryDelayI18n == null || typeof parsed.deliveryDelayI18n !== "object") {
            parsed.deliveryDelayI18n = {};
          }
          if (parsed.deliveryDelayNotice != null && typeof parsed.deliveryDelayNotice !== "object") {
            parsed.deliveryDelayNotice = null;
          }
          return parsed;
        } catch (e) {
          console.warn("CustomBundleBuilder: invalid JSON config", e);
          return null;
        }
      }

      #totalSteps() {
        return this.#config.stepMinPicks.length;
      }

      #rememberSelectionEnabled() {
        return String(this.dataset.rememberSelection || "") === "true";
      }

      #continueUsesNextStepTitle() {
        return String(this.dataset.continueUseNextStepTitle || "") === "true";
      }

      #selectionStorageKey() {
        const sid = (this.dataset.sectionId || "").trim();
        return sid !== "" ? `cbbBundleSelection:${sid}` : "cbbBundleSelection:default";
      }

      #schedulePersistSelectionToStorage() {
        if (!this.#rememberSelectionEnabled()) return;
        if (this.#selectionPersistTimer != null) {
          clearTimeout(this.#selectionPersistTimer);
        }
        this.#selectionPersistTimer = window.setTimeout(() => {
          this.#selectionPersistTimer = null;
          this.#writeSelectionToStorage();
        }, 250);
      }

      #writeSelectionToStorage() {
        if (!this.#rememberSelectionEnabled()) return;
        const key = this.#selectionStorageKey();
        const store = typeof window !== "undefined" ? window.sessionStorage : null;
        if (!store) return;
        try {
          if (this.#selection.size === 0) {
            store.removeItem(key);
            try {
              window.localStorage?.removeItem(key);
            } catch (e) {
              /* ignore */
            }
            return;
          }
          /** @type {Array<[string, { stepIndex: number; quantity: number }]>} */
          const entries = [];
          for (const [id, row] of this.#selection) {
            const q = Number.isFinite(row.quantity) && row.quantity > 0 ? row.quantity : 1;
            entries.push([id, { stepIndex: row.stepIndex, quantity: q }]);
          }
          store.setItem(
            key,
            JSON.stringify({ v: 2, furthestVisitedStep: this.#furthestVisitedStep, entries }),
          );
        } catch (e) {
          /* sessionStorage unavailable (private mode, quota, etc.) */
        }
      }

      #loadPersistedSelection() {
        if (!this.#rememberSelectionEnabled()) return;

        const key = this.#selectionStorageKey();

        try {
          window.localStorage?.removeItem(key);
        } catch (e) {
          /* ignore */
        }

        const store = typeof window !== "undefined" ? window.sessionStorage : null;
        if (!store) return;

        /** @type {string | null} */
        let raw;
        try {
          raw = store.getItem(key);
        } catch (e) {
          return;
        }
        if (!raw) return;

        /** @type {unknown} */
        let parsed;
        try {
          parsed = JSON.parse(raw);
        } catch (e) {
          return;
        }

        if (!parsed || typeof parsed !== "object" || !("entries" in parsed) || !Array.isArray(/** @type {{ entries: unknown }} */ (parsed).entries)) {
          return;
        }

        const vRaw = /** @type {{ v?: unknown }} */ (parsed).v;
        if (vRaw !== 1 && vRaw !== 2) return;

        /** @type {1 | 2} */
        const storageSchema = vRaw;

        let restoredAny = false;

        for (const row of /** @type {{ entries: unknown[] }} */ (parsed).entries) {
          if (!Array.isArray(row) || row.length < 2) continue;
          const variantId = String(row[0] ?? "").trim();
          const meta = row[1];
          if (!variantId || !meta || typeof meta !== "object") continue;
          const savedQty = Number(/** @type {{ quantity?: unknown }} */ (meta).quantity);
          const qty = Number.isFinite(savedQty) && savedQty > 0 ? Math.floor(savedQty) : 1;

          const esc =
            typeof CSS !== "undefined" && typeof CSS.escape === "function"
              ? CSS.escape(variantId)
              : variantId.replace(/["\\]/g, "\\$&");
          const card = this.querySelector(`[data-cbb-product-card][data-variant-id="${esc}"]`);
          if (!(card instanceof HTMLElement)) continue;

          const priceRaw = card.getAttribute("data-price-cents");
          const stepRaw = card.getAttribute("data-step-index");
          if (priceRaw == null || stepRaw == null) continue;

          const priceCents = parseInt(priceRaw, 10);
          const stepIndex = parseInt(stepRaw, 10);
          if (Number.isNaN(priceCents) || Number.isNaN(stepIndex)) continue;

          const imgEl = card.querySelector(".custom-bundle-builder-v2__card-img");
          /** @type {string | undefined} */
          let imageUrl;
          /** @type {string | undefined} */
          let imageAlt;
          if (imgEl instanceof HTMLImageElement) {
            const u = (imgEl.currentSrc || imgEl.src || "").trim();
            if (u) imageUrl = u;
            const alt = (imgEl.alt || "").trim();
            if (alt) imageAlt = alt;
          }

          const link = card.querySelector("[data-cbb-product-link]");
          const titleText = link
            ? (link.textContent || "").replace(/\s+/g, " ").trim()
            : (card.querySelector(".custom-bundle-builder-v2__card-title")?.textContent || "")
              .replace(/\s+/g, " ")
              .trim();

          /** @type {{ priceCents: number; stepIndex: number; quantity: number; productId?: number; imageUrl?: string; imageAlt?: string; title?: string; mediaBgColor?: string; mediaBgImage?: string }} */
          const restored = {
            priceCents,
            stepIndex,
            quantity: qty,
            ...(imageUrl ? { imageUrl } : {}),
            ...(imageAlt ? { imageAlt } : {}),
            ...(titleText ? { title: titleText } : {}),
          };
          const productIdRaw = card.getAttribute("data-product-id");
          const productId = productIdRaw ? Number(productIdRaw) : NaN;
          if (Number.isFinite(productId)) {
            restored.productId = productId;
          }
          this.#applyCardMediaMetaToEntry(card, restored);
          this.#selection.set(variantId, restored);
          restoredAny = true;
        }

        const total = this.#totalSteps();
        if (restoredAny && total > 0) {
          if (storageSchema === 2) {
            const fRaw = /** @type {{ furthestVisitedStep?: unknown }} */ (parsed).furthestVisitedStep;
            let fParsed = typeof fRaw === "number" ? fRaw : parseInt(String(fRaw ?? "").trim(), 10);
            if (Number.isFinite(fParsed) && fParsed >= 1) {
              this.#furthestVisitedStep = Math.min(total, Math.max(1, Math.floor(fParsed)));
            } else {
              /** Corrupt or legacy partial payloads: fall back to SKUs touched in the persisted cart */
              this.#syncFurthestVisitedFromHighestSelectedStep();
            }
          } else {
            /** Legacy v1 — no furthest snapshot; never treat “mins satisfied” as having opened later optional steps */
            this.#syncFurthestVisitedFromHighestSelectedStep();
          }
        }

        this.#restoredSelectionNeedsBiscuitsSync = Boolean(this.#biscuitsBridgeActive() && restoredAny);
      }

      /**
       * Parse `?bundle=` using Biscuits-style encoding: comma-separated `variantId:quantity` (quantity defaults to 1).
       * @returns {Array<{ variantId: string; quantity: number }>}
       */
      #parseBundleQueryParamEntries() {
        if (typeof window === "undefined") return [];

        let raw = "";
        try {
          raw = new URLSearchParams(window.location.search).get("bundle") || "";
        } catch (e) {
          return [];
        }

        raw = raw.trim();
        if (!raw) return [];

        /** @type {Map<string, number>} */
        const byId = new Map();

        const segments = raw.split(",");
        for (let seg of segments) {
          seg = seg.trim().replace(/%3A/gi, ":");
          if (!seg) continue;

          const colon = seg.indexOf(":");
          let idPart;
          let qtyPart;
          if (colon === -1) {
            idPart = seg;
            qtyPart = "1";
          } else {
            idPart = seg.slice(0, colon).trim();
            qtyPart = seg.slice(colon + 1).trim();
          }
          if (!idPart) continue;

          const qtyNum = Number(qtyPart);
          const quantity = Number.isFinite(qtyNum) && qtyNum > 0 ? Math.floor(qtyNum) : 1;
          const prev = byId.get(idPart) || 0;
          byId.set(idPart, prev + quantity);
        }

        return [...byId.entries()].map(([variantId, quantity]) => ({ variantId, quantity }));
      }

      /**
       * When the URL includes `bundle=`, pre-select matching cards (overrides session restore). No-op if nothing matches.
       */
      #applyBundleQueryParamPrefill() {
        const parsed = this.#parseBundleQueryParamEntries();
        if (parsed.length === 0) return;

        /** @type {Map<string, { priceCents: number; stepIndex: number; quantity: number; imageUrl?: string; imageAlt?: string; title?: string; mediaBgColor?: string; mediaBgImage?: string }>} */
        const next = new Map();

        for (const { variantId, quantity } of parsed) {
          const key = String(variantId).trim();
          if (!key) continue;

          const esc =
            typeof CSS !== "undefined" && typeof CSS.escape === "function"
              ? CSS.escape(key)
              : key.replace(/["\\]/g, "\\$&");
          const card = this.querySelector(`[data-cbb-product-card][data-variant-id="${esc}"]`);
          if (!(card instanceof HTMLElement)) continue;

          const priceRaw = card.getAttribute("data-price-cents");
          const stepRaw = card.getAttribute("data-step-index");
          if (priceRaw == null || stepRaw == null) continue;

          const priceCents = parseInt(priceRaw, 10);
          const stepIndex = parseInt(stepRaw, 10);
          if (Number.isNaN(priceCents) || Number.isNaN(stepIndex)) continue;

          const imgEl = card.querySelector(".custom-bundle-builder-v2__card-img");
          /** @type {string | undefined} */
          let imageUrl;
          /** @type {string | undefined} */
          let imageAlt;
          if (imgEl instanceof HTMLImageElement) {
            const u = (imgEl.currentSrc || imgEl.src || "").trim();
            if (u) imageUrl = u;
            const alt = (imgEl.alt || "").trim();
            if (alt) imageAlt = alt;
          }

          const link = card.querySelector("[data-cbb-product-link]");
          const titleText = link
            ? (link.textContent || "").replace(/\s+/g, " ").trim()
            : (card.querySelector(".custom-bundle-builder-v2__card-title")?.textContent || "")
              .replace(/\s+/g, " ")
              .trim();

          /** @type {{ priceCents: number; stepIndex: number; quantity: number; productId?: number; imageUrl?: string; imageAlt?: string; title?: string; mediaBgColor?: string; mediaBgImage?: string }} */
          const entry = {
            priceCents,
            stepIndex,
            quantity,
            ...(imageUrl ? { imageUrl } : {}),
            ...(imageAlt ? { imageAlt } : {}),
            ...(titleText ? { title: titleText } : {}),
          };
          const productIdRaw = card.getAttribute("data-product-id");
          const productId = productIdRaw ? Number(productIdRaw) : NaN;
          if (Number.isFinite(productId)) {
            entry.productId = productId;
          }
          this.#applyCardMediaMetaToEntry(card, entry);
          next.set(key, entry);
        }

        if (next.size === 0) return;

        this.#selection.clear();
        for (const [k, v] of next) {
          this.#selection.set(k, v);
        }

        this.#tierScrollPending = true;
        this.#restoredSelectionNeedsBiscuitsSync = Boolean(this.#biscuitsBridgeActive());

        /** URL-share bundles imply progress only up to included step indices — never finalize via mins alone */
        this.#syncFurthestVisitedFromHighestSelectedStep();
      }

      /**
       * After Biscuits mounts step products, mirror restored Map into app controls (initial click + quantity).
       */
      #syncRestoredSelectionToBiscuitsWhenReady() {
        if (!this.#biscuitsBridgeActive() || !this.#restoredSelectionNeedsBiscuitsSync) return;
        const root = this.#getSectionRoot();
        if (!root || !this.#sectionHasBiscuitsStepProducts(root)) return;

        this.#restoredSelectionNeedsBiscuitsSync = false;

        for (const [variantId, entry] of this.#selection) {
          const esc =
            typeof CSS !== "undefined" && typeof CSS.escape === "function"
              ? CSS.escape(variantId)
              : variantId.replace(/["\\]/g, "\\$&");
          const card = this.querySelector(`[data-cbb-product-card][data-variant-id="${esc}"]`);
          if (!(card instanceof HTMLElement)) continue;

          this.#bridgeSyncBiscuitsProduct(card);

          const q = Number.isFinite(entry.quantity) && entry.quantity > 0 ? entry.quantity : 1;
          for (let i = 1; i < q; i += 1) {
            if (!this.#bridgeClickBiscuitsQuantity(card, "plus")) break;
          }
        }

        this.#tierScrollPending = true;
        this.#refreshUI();
      }

      #nextStepTitleText() {
        const nextIdx = this.#currentStep + 1;
        const fromConfig = this.#config?.stepLabels?.[nextIdx - 1];
        if (typeof fromConfig === "string") {
          const trimmed = fromConfig.replace(/\s+/g, " ").trim();
          if (trimmed !== "") return trimmed;
        }

        const stepperEl = this.querySelector(`[data-cbb-stepper-index="${nextIdx}"]`);
        if (!(stepperEl instanceof Element)) return "";
        const fromAria = (stepperEl.getAttribute("aria-label") || "").replace(/\s+/g, " ").trim();
        if (fromAria !== "") return fromAria;
        const titleEl = stepperEl.querySelector(".custom-bundle-builder-v2__stepper-title");
        return titleEl ? (titleEl.textContent || "").replace(/\s+/g, " ").trim() : "";
      }

      #scheduleInitSwiper(stepIndex) {
        if (!this.#swiperEnabled()) {
          this.#reapplyTopicFilterForStep(stepIndex);
          return;
        }
        // Double rAF: wait until layout.width is settled (grid/container + fonts) before Swiper measures.
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.#ensureSwiper(stepIndex);
          });
        });
      }

      #syncCbbSwiperNav(sw) {
        if (!sw || !sw.el) return;
        const panel = sw.el.closest("[data-cbb-step-panel]");
        const navWrap = panel?.querySelector("[data-cbb-swiper-nav-wrap]");
        if (!(navWrap instanceof HTMLElement)) return;

        const locked = Boolean(sw.isLocked);
        navWrap.hidden = locked;
        navWrap.setAttribute("aria-hidden", locked ? "true" : "false");
      }

      /** Mobile bullet pagination: hide host when Swiper locks overflow or a single page (no usable pagination). */
      #syncCbbSwiperPagination(sw) {
        if (!sw || !sw.el) return;
        if (!this.#isNarrowBundleLayout()) return;

        const panel = sw.el.closest("[data-cbb-step-panel]");
        const mobileHost = panel?.querySelector("[data-cbb-swiper-pagination-host-mobile]");
        if (!(mobileHost instanceof HTMLElement)) return;

        const locked = Boolean(sw.isLocked);
        const rawBullets = sw.pagination?.bullets;
        const bulletCount =
          rawBullets && typeof rawBullets.length === "number" ? rawBullets.length : 0;
        const inactive = locked || bulletCount <= 1;

        mobileHost.hidden = inactive;
        mobileHost.setAttribute("aria-hidden", inactive ? "true" : "false");
      }

      #syncCbbSwiperUi(sw) {
        this.#syncCbbSwiperNav(sw);
        this.#syncCbbSwiperPagination(sw);
      }

      #ensureSwiper(stepIndex) {
        if (!this.#swiperEnabled()) {
          this.#reapplyTopicFilterForStep(stepIndex);
          return;
        }
        if (this.#swipers.has(stepIndex)) {
          const inst = this.#swipers.get(stepIndex);
          this.#reapplyTopicFilterForStep(stepIndex);
          if (inst && typeof inst.update === "function") inst.update();
          if (inst) this.#syncCbbSwiperUi(inst);
          return;
        }

        const root = this.querySelector(`[data-cbb-swiper-root="${stepIndex}"]`);
        if (!root || typeof Swiper === "undefined") {
          this.#reapplyTopicFilterForStep(stepIndex);
          return;
        }

        const panel = this.querySelector(`[data-cbb-step-panel="${stepIndex}"]`);
        if (!panel || panel.hidden) return;

        this.#dockSwiperPaginationInPanel(panel);

        const prevEl = panel.querySelector("[data-cbb-swiper-prev]");
        const nextEl = panel.querySelector("[data-cbb-swiper-next]");
        const paginationEl = panel.querySelector("[data-cbb-swiper-pagination]");
        const navOk = prevEl instanceof HTMLElement && nextEl instanceof HTMLElement;
        const paginationOk = paginationEl instanceof HTMLElement;
        const stackedLayout = this.#isNarrowBundleLayout();

        const groupByView = this.#swiperNavGroupByView();

        try {
          const swiper = new Swiper(root, {
            slidesPerView: 2,
            spaceBetween: 5,
            ...(groupByView ? { slidesPerGroup: 2 } : {}),
            grid: {
              rows: 2,
              fill: "row",
            },
            watchOverflow: true,
            breakpointsBase: "window",
            breakpoints: {
              576: {
                slidesPerView: 3,
                spaceBetween: 14,
                ...(groupByView ? { slidesPerGroup: 3 } : {}),
              },
              1200: {
                slidesPerView: 4,
                spaceBetween: 16,
                ...(groupByView ? { slidesPerGroup: 4 } : {}),
              },
            },
            navigation: navOk
              ? {
                prevEl,
                nextEl,
              }
              : undefined,
            pagination: paginationOk
              ? stackedLayout
                ? {
                  el: paginationEl,
                  clickable: true,
                  type: "bullets",
                }
                : {
                  el: paginationEl,
                  type: "progressbar",
                }
              : undefined,
            on: {
              init: (sw) => {
                requestAnimationFrame(() => {
                  if (typeof sw.update === "function") sw.update();
                  this.#syncCbbSwiperUi(sw);
                });
              },
              resize: (sw) => this.#syncCbbSwiperUi(sw),
              breakpoint: (sw) => this.#syncCbbSwiperUi(sw),
              update: (sw) => this.#syncCbbSwiperUi(sw),
              lock: (sw) => this.#syncCbbSwiperUi(sw),
              unlock: (sw) => this.#syncCbbSwiperUi(sw),
            },
          });
          this.#swipers.set(stepIndex, swiper);
          this.#reapplyTopicFilterForStep(stepIndex);
          if (typeof swiper.update === "function") swiper.update();
          this.#syncCbbSwiperUi(swiper);
        } catch (e) {
          console.warn("CustomBundleBuilder: Swiper init failed", e);
          this.#swipers.set(stepIndex, null);
          this.#reapplyTopicFilterForStep(stepIndex);
        }
      }

      #destroySwipers() {
        for (const [, inst] of this.#swipers) {
          if (inst && typeof inst.destroy === "function") {
            try {
              inst.destroy(true, true);
            } catch (e) {
              /* ignore */
            }
          }
        }
        this.#swipers.clear();
      }

      /** @param {HTMLElement} panel */
      #collectTopicFilterGroupRoots(panel) {
        const named = [...panel.querySelectorAll("[data-cbb-topic-filter-group]")];
        if (named.length) {
          return named.map((root) => ({
            key: root.getAttribute("data-cbb-topic-filter-group") || "primary",
            root,
          }));
        }
        const legacyHost = panel.querySelector("[data-cbb-topic-filters]");
        if (legacyHost instanceof HTMLElement) return [{ key: "primary", root: legacyHost }];
        if (panel.querySelector("[data-cbb-topic-filter]")) return [{ key: "primary", root: panel }];
        return [];
      }

      /** @param {number} stepIndex */
      #getStepTopicFilterGroupMap(stepIndex) {
        let m = this.#topicFilterGroupsByStep.get(stepIndex);
        if (!m) {
          m = new Map();
          this.#topicFilterGroupsByStep.set(stepIndex, m);
        }
        return m;
      }

      /**
       * @param {HTMLElement} panel
       * @param {Map<string, string>} groupMap
       * @returns {string[]}
       */
      #resolveRequiredTopicTagNorms(panel, groupMap) {
        const groups = this.#collectTopicFilterGroupRoots(panel);
        const required = [];
        for (const { key, root } of groups) {
          const idFromMap = groupMap.get(key);
          let btn =
            typeof idFromMap === "string" && idFromMap !== ""
              ? root.querySelector(`[data-cbb-topic-filter-id="${idFromMap}"]`)
              : null;
          if (!(btn instanceof HTMLElement)) {
            btn =
              root.querySelector(".custom-bundle-builder-v2__topic-filter--active") ||
              root.querySelector('[data-cbb-topic-filter][aria-pressed="true"]') ||
              root.querySelector("[data-cbb-topic-filter]");
          }
          if (!(btn instanceof HTMLElement)) continue;
          const mode = btn.getAttribute("data-cbb-topic-filter-mode") || "all";
          const rawTag = btn.getAttribute("data-cbb-topic-filter-tag") || "";
          const tagNorm = rawTag.trim().toLowerCase();
          if (mode === "tag" && tagNorm) required.push(tagNorm);
        }
        return required;
      }

      /** @param {number} stepIndex */
      #applyCombinedTopicFiltersForStep(stepIndex) {
        const panel = this.querySelector(`[data-cbb-step-panel="${stepIndex}"]`);
        if (!(panel instanceof HTMLElement)) return;

        const groups = this.#collectTopicFilterGroupRoots(panel);
        if (!groups.length) return;

        const groupMap = this.#getStepTopicFilterGroupMap(stepIndex);
        const requiredTags = this.#resolveRequiredTopicTagNorms(panel, groupMap);

        const root = panel.querySelector(`[data-cbb-step-cards="${stepIndex}"]`);
        if (!root) return;

        const wrap = this.#swiperEnabled() ? root.querySelector(".swiper-wrapper") : root;
        if (!(wrap instanceof HTMLElement)) return;

        wrap.querySelectorAll(":scope > *").forEach((node) => {
          if (!(node instanceof HTMLElement)) return;
          const card = node.querySelector("[data-cbb-product-card]");
          if (!(card instanceof HTMLElement)) {
            if (this.#swiperEnabled()) {
              node.classList.add("swiper-slide");
              node.classList.remove("custom-bundle-builder-v2__swiper-slide--hidden");
            } else {
              node.classList.remove("custom-bundle-builder-v2__grid-cell--hidden");
            }
            return;
          }

          let tags = [];
          const rawTags = card.getAttribute("data-cbb-product-tags") || "[]";
          try {
            const parsed = JSON.parse(rawTags);
            if (Array.isArray(parsed)) tags = parsed;
          } catch (e) {
            tags = [];
          }
          const normalized = new Set(tags.map((x) => String(x).trim().toLowerCase()).filter(Boolean));
          const show =
            requiredTags.length === 0 || requiredTags.every((tag) => normalized.has(tag));

          if (show) {
            if (this.#swiperEnabled()) {
              node.classList.add("swiper-slide");
              node.classList.remove("custom-bundle-builder-v2__swiper-slide--hidden");
            } else {
              node.classList.remove("custom-bundle-builder-v2__grid-cell--hidden");
            }
          } else if (this.#swiperEnabled()) {
            node.classList.remove("swiper-slide");
            node.classList.add("custom-bundle-builder-v2__swiper-slide--hidden");
          } else {
            node.classList.add("custom-bundle-builder-v2__grid-cell--hidden");
          }
        });

        const swiper = this.#swipers.get(stepIndex);
        if (swiper) {
          const visibleCount = wrap.querySelectorAll(".swiper-slide").length;
          requestAnimationFrame(() => {
            if (typeof swiper.updateSlides === "function") swiper.updateSlides();
            if (typeof swiper.updateSize === "function") swiper.updateSize();
            if (typeof swiper.updateProgress === "function") swiper.updateProgress();
            if (typeof swiper.update === "function") swiper.update();
            if (typeof swiper.slideTo === "function" && visibleCount > 0) {
              swiper.slideTo(0, 0);
            }
            if (swiper.pagination && typeof swiper.pagination.update === "function") {
              swiper.pagination.update();
            }
            if (swiper.navigation && typeof swiper.navigation.update === "function") {
              swiper.navigation.update();
            }
            this.#syncCbbSwiperUi(swiper);
          });
        }
      }

      /** @param {number} stepIndex */
      #reapplyTopicFilterForStep(stepIndex) {
        const panel = this.querySelector(`[data-cbb-step-panel="${stepIndex}"]`);
        if (!(panel instanceof HTMLElement)) return;

        const groups = this.#collectTopicFilterGroupRoots(panel);
        if (!groups.length) return;

        const groupMap = this.#getStepTopicFilterGroupMap(stepIndex);

        for (const { key, root } of groups) {
          const idFromMap = groupMap.get(key);
          let btn =
            typeof idFromMap === "string" && idFromMap !== ""
              ? root.querySelector(`[data-cbb-topic-filter-id="${idFromMap}"]`)
              : null;
          if (!(btn instanceof HTMLElement)) {
            btn =
              root.querySelector('[data-cbb-topic-filter][aria-pressed="true"]') ||
              root.querySelector("[data-cbb-topic-filter]");
          }
          if (!(btn instanceof HTMLElement)) continue;

          const filterId = btn.getAttribute("data-cbb-topic-filter-id") || "";
          groupMap.set(key, filterId);

          root.querySelectorAll("[data-cbb-topic-filter]").forEach((b) => {
            if (!(b instanceof HTMLElement)) return;
            const on = b === btn;
            b.classList.toggle("custom-bundle-builder-v2__topic-filter--active", on);
            b.setAttribute("aria-pressed", on ? "true" : "false");
          });
        }

        this.#applyCombinedTopicFiltersForStep(stepIndex);
      }

      /** @param {HTMLElement} btn */
      #topicFilterFromButton(btn) {
        const panel = btn.closest("[data-cbb-step-panel]");
        if (!(panel instanceof HTMLElement)) return;

        const stepRaw = panel.getAttribute("data-cbb-step-panel");
        const stepIndex = parseInt(stepRaw || "0", 10);
        if (Number.isNaN(stepIndex) || stepIndex < 1) return;

        let groupRoot = btn.closest("[data-cbb-topic-filter-group]");
        if (!(groupRoot instanceof HTMLElement)) {
          const legacy = btn.closest("[data-cbb-topic-filters]");
          groupRoot = legacy instanceof HTMLElement ? legacy : btn.closest(".custom-bundle-builder-v2__topic-filters-row");
        }
        if (!(groupRoot instanceof HTMLElement)) return;
        const groupKey =
          groupRoot instanceof HTMLElement && groupRoot.hasAttribute("data-cbb-topic-filter-group")
            ? groupRoot.getAttribute("data-cbb-topic-filter-group") || "primary"
            : "primary";

        const filterId = btn.getAttribute("data-cbb-topic-filter-id") || "";
        this.#getStepTopicFilterGroupMap(stepIndex).set(groupKey, filterId);

        groupRoot.querySelectorAll("[data-cbb-topic-filter]").forEach((b) => {
          if (!(b instanceof HTMLElement)) return;
          const on = b === btn;
          b.classList.toggle("custom-bundle-builder-v2__topic-filter--active", on);
          b.setAttribute("aria-pressed", on ? "true" : "false");
        });

        this.#applyCombinedTopicFiltersForStep(stepIndex);
      }

      /** @param {MouseEvent} e */
      #onClick(e) {
        const t = e.target;
        if (!(t instanceof Element)) return;

        const jumper = t.closest("[data-cbb-stepper-jump]");
        if (jumper instanceof HTMLElement) {
          if (jumper.getAttribute("aria-disabled") !== "false") return;
          const target = parseInt(jumper.getAttribute("data-cbb-stepper-jump") || "0", 10);

          if (this.#stepperJumpAllowed(target)) {
            e.preventDefault();
            this.#setBuilderError(false);
            this.#goToStep(target);
          }

          return;
        }

        const introAccToggle = t.closest("[data-cbb-intro-accordion-toggle]");
        if (introAccToggle instanceof HTMLButtonElement) {
          e.preventDefault();
          this.#toggleIntroAccordion(introAccToggle);
          return;
        }

        const topicFilterBtn = t.closest("[data-cbb-topic-filter]");
        if (topicFilterBtn instanceof HTMLElement) {
          e.preventDefault();
          this.#topicFilterFromButton(topicFilterBtn);
          return;
        }

        const tierRm = t.closest("[data-cbb-tier-remove]");
        if (tierRm instanceof HTMLElement) {
          e.preventDefault();
          e.stopPropagation();
          const slotRaw = tierRm.getAttribute("data-cbb-tier-slot-index") || "0";
          const slotIndex = parseInt(slotRaw, 10);
          if (Number.isFinite(slotIndex) && slotIndex > 0) {
            this.#onTierFilledCellClick(slotIndex);
          }
          return;
        }

        if (t.closest("[data-cbb-product-link]")) return;

        const qtyPlusEl = t.closest("[data-cbb-qty-plus]");
        const qtyMinusEl = t.closest("[data-cbb-qty-minus]");
        if (qtyPlusEl || qtyMinusEl) {
          e.preventDefault();
          e.stopPropagation();
          const card = (qtyPlusEl || qtyMinusEl)?.closest("[data-cbb-product-card]");
          if (card instanceof HTMLElement) {
            this.#adjustCardQuantity(card, qtyPlusEl ? 1 : -1);
          }
          return;
        }

        const addBtn = t.closest("[data-cbb-add]");
        if (addBtn) {
          e.preventDefault();
          e.stopPropagation();
          this.#toggleCard(addBtn);
          return;
        }

        if (t.closest("[data-cbb-product-card]") && t.closest("[data-cbb-qty-wrap]")) return;

        const card = t.closest("[data-cbb-product-card]");
        if (card instanceof HTMLElement) {
          const nestedLink = card.querySelector("[data-cbb-product-link]");
          if (nestedLink && nestedLink.contains(t)) return;

          const btnInCard = card.querySelector("[data-cbb-add]");
          if (btnInCard instanceof HTMLElement) {
            e.preventDefault();
            this.#toggleCard(btnInCard);
          }
          return;
        }

        const backCta = t.closest("[data-cbb-back-cta]");
        if (backCta instanceof HTMLButtonElement) {
          if (backCta.disabled) return;
          e.preventDefault();
          this.#setBuilderError(false);
          this.#goToStep(this.#currentStep - 1);
          return;
        }

        const removeAllBtn = t.closest("[data-cbb-remove-all]");
        if (removeAllBtn instanceof HTMLElement) {
          e.preventDefault();
          if (this.#selection.size === 0) return;
          this.#clearEntireSelection();
          this.#resetIntroAccordionPanel();
          return;
        }

        const copyBundleLinkBtn = t.closest("[data-cbb-copy-bundle-link]");
        if (copyBundleLinkBtn instanceof HTMLButtonElement) {
          e.preventDefault();
          this.#copyBundleLink();
          return;
        }

        const cta = t.closest("[data-cbb-primary-cta]");
        if (cta) {
          e.preventDefault();
          this.#onPrimaryCta();
        }
      }

      /** @param {KeyboardEvent} e */
      #onKeyDown(e) {
        if (e.key === "Escape") {
          if (this.#isNarrowBundleLayout()) {
            const wrap = this.querySelector("[data-cbb-intro-accordion]");
            if (
              wrap instanceof HTMLElement &&
              wrap.classList.contains("custom-bundle-builder-v2__intro-actions-accordion--open")
            ) {
              e.preventDefault();
              this.#resetIntroAccordionPanel();
            }
          }
          return;
        }

        if (e.key !== "Enter" && e.key !== " ") return;
        const t = e.target;
        if (!(t instanceof Element)) return;

        const jumper = t.closest("[data-cbb-stepper-jump]");
        if (jumper instanceof HTMLElement && jumper.getAttribute("aria-disabled") === "false") {
          const target = parseInt(jumper.getAttribute("data-cbb-stepper-jump") || "0", 10);

          if (this.#stepperJumpAllowed(target)) {
            e.preventDefault();
            this.#setBuilderError(false);
            this.#goToStep(target);
          }

          return;
        }

        const qtyPlusEl = t.closest("[data-cbb-qty-plus]");
        const qtyMinusEl = t.closest("[data-cbb-qty-minus]");
        if (qtyPlusEl || qtyMinusEl) {
          e.preventDefault();
          const card = (qtyPlusEl || qtyMinusEl)?.closest("[data-cbb-product-card]");
          if (card instanceof HTMLElement) {
            this.#adjustCardQuantity(card, qtyPlusEl ? 1 : -1);
          }
          return;
        }

        const addBtn = t.closest("[data-cbb-add]");
        if (addBtn) {
          e.preventDefault();
          this.#toggleCard(addBtn);
          return;
        }

        const tierRmKb = t.closest("[data-cbb-tier-remove]");
        if (tierRmKb instanceof HTMLElement) {
          e.preventDefault();
          const slotRaw = tierRmKb.getAttribute("data-cbb-tier-slot-index") || "0";
          const slotIndex = parseInt(slotRaw, 10);
          if (Number.isFinite(slotIndex) && slotIndex > 0) {
            this.#onTierFilledCellClick(slotIndex);
          }
          return;
        }
      }

      /** @param {Element} addBtn */
      #toggleCard(addBtn) {
        const card = addBtn.closest("[data-cbb-product-card]");
        if (!card) return;

        const variantId = card.getAttribute("data-variant-id");
        const priceRaw = card.getAttribute("data-price-cents");
        const stepRaw = card.getAttribute("data-step-index");

        if (!variantId || priceRaw == null || stepRaw == null) return;

        const priceCents = parseInt(priceRaw, 10);
        const stepIndex = parseInt(stepRaw, 10);

        if (Number.isNaN(priceCents) || Number.isNaN(stepIndex)) return;

        const key = String(variantId);

        if (this.#selection.has(key)) {
          const priorStep = this.#currentStep;
          this.#selection.delete(key);
          card.classList.remove("custom-bundle-builder-v2__card--selected");
          addBtn.setAttribute("aria-pressed", "false");
          if (this.#selection.size === 0) {
            this.#tierScrollPending = true;
            this.#setBuilderError(false);
            this.#finalizeEmptyBundleNavigation(priorStep);
            this.#bridgeSyncBiscuitsProduct(card);
            return;
          }
        } else {
          const imgEl = card.querySelector(".custom-bundle-builder-v2__card-img");
          /** @type {string | undefined} */
          let imageUrl;
          /** @type {string | undefined} */
          let imageAlt;
          if (imgEl instanceof HTMLImageElement) {
            const u = (imgEl.currentSrc || imgEl.src || "").trim();
            if (u) imageUrl = u;
            const alt = (imgEl.alt || "").trim();
            if (alt) imageAlt = alt;
          }
          const link = card.querySelector("[data-cbb-product-link]");
          const titleText = link
            ? (link.textContent || "").replace(/\s+/g, " ").trim()
            : (card.querySelector(".custom-bundle-builder-v2__card-title")?.textContent || "")
              .replace(/\s+/g, " ")
              .trim();
          /** @type {{ priceCents: number; stepIndex: number; quantity: number; productId?: number; imageUrl?: string; imageAlt?: string; title?: string; mediaBgColor?: string; mediaBgImage?: string }} */
          const entry = {
            priceCents,
            stepIndex,
            quantity: 1,
            ...(imageUrl ? { imageUrl } : {}),
            ...(imageAlt ? { imageAlt } : {}),
            ...(titleText ? { title: titleText } : {}),
          };
          const productIdRaw = card.getAttribute("data-product-id");
          const productId = productIdRaw ? Number(productIdRaw) : NaN;
          if (Number.isFinite(productId)) {
            entry.productId = productId;
          }
          this.#applyCardMediaMetaToEntry(card, entry);
          this.#selection.set(key, entry);
          card.classList.add("custom-bundle-builder-v2__card--selected");
          addBtn.setAttribute("aria-pressed", "true");
          this.#tierScrollPending = true;
        }

        this.#refreshUI();
        this.#bridgeSyncBiscuitsProduct(card);
      }

      /**
       * Clicks Biscuits quantity control inside the matched product host when bridge is active.
       * @param {HTMLElement} card
       * @param {"minus"|"plus"} which
       * @returns {boolean} true when a clickable app control fired
       */
      #bridgeClickBiscuitsQuantity(card, which) {
        if (!this.#biscuitsBridgeActive()) return true;
        const variantId = card.getAttribute("data-variant-id");
        if (!variantId) return false;
        const host = this.#findBiscuitsProductHostForVariant(variantId);
        if (!(host instanceof HTMLElement)) return false;
        const btn =
          which === "minus"
            ? host.querySelector(".biscuits-bundle-quantity__button--minus")
            : host.querySelector(".biscuits-bundle-quantity__button--plus");
        if (!(btn instanceof HTMLElement)) return false;
        if (btn.classList.contains("biscuits--disabled")) return false;
        btn.click();
        return true;
      }

      /** @param {HTMLElement} card @param {1 | -1} delta */
      #adjustCardQuantity(card, delta) {
        const variantId = card.getAttribute("data-variant-id");
        if (!variantId) return;

        const key = String(variantId);
        const entry = this.#selection.get(key);
        if (!entry) return;

        const qty = Number.isFinite(entry.quantity) && entry.quantity > 0 ? entry.quantity : 1;
        const priorStep = this.#currentStep;

        if (delta > 0) {
          const which = "plus";
          if (!this.#bridgeClickBiscuitsQuantity(card, which)) return;
          entry.quantity = qty + 1;
          const imgEl = card.querySelector(".custom-bundle-builder-v2__card-img");
          if (imgEl instanceof HTMLImageElement) {
            const u = (imgEl.currentSrc || imgEl.src || "").trim();
            if (u) entry.imageUrl = u;
            const alt = (imgEl.alt || "").trim();
            if (alt) entry.imageAlt = alt;
          }
          this.#tierScrollPending = true;
          this.#refreshUI();
          return;
        }

        const which = "minus";

        if (this.#biscuitsBridgeActive()) {
          const bridged = this.#bridgeClickBiscuitsQuantity(card, which);
          if (!bridged) return;
          if (qty <= 1) {
            this.#selection.delete(key);
            card.classList.remove("custom-bundle-builder-v2__card--selected");
            const addBtn = card.querySelector("[data-cbb-add]");
            if (addBtn instanceof HTMLElement) {
              addBtn.setAttribute("aria-pressed", "false");
            }
          } else {
            entry.quantity = qty - 1;
          }
          if (this.#selection.size === 0) {
            this.#tierScrollPending = true;
            this.#setBuilderError(false);
            this.#finalizeEmptyBundleNavigation(priorStep);
          } else {
            this.#refreshUI();
          }
          return;
        }

        if (qty <= 1) {
          this.#selection.delete(key);
          card.classList.remove("custom-bundle-builder-v2__card--selected");
          const addBtn = card.querySelector("[data-cbb-add]");
          if (addBtn instanceof HTMLElement) {
            addBtn.setAttribute("aria-pressed", "false");
          }
        } else {
          entry.quantity = qty - 1;
        }
        if (this.#selection.size === 0) {
          this.#tierScrollPending = true;
          this.#setBuilderError(false);
          this.#finalizeEmptyBundleNavigation(priorStep);
        } else {
          this.#refreshUI();
        }
      }

      /** @param {boolean} visible @param {string} [message] */
      #setBuilderError(visible, message) {
        const el = this.querySelector("[data-cbb-builder-error]");
        if (!(el instanceof HTMLElement)) return;

        if (!visible) {
          el.textContent = "";
          el.hidden = true;
          return;
        }

        el.textContent = message !== undefined && String(message).trim() !== "" ? String(message) : "";
        el.hidden = String(el.textContent).trim() === "";
      }

      /** @returns {boolean} User has opened the final step at least once (Continue or stepper). */
      #hasVisitedLastBuilderStep() {
        return this.#furthestVisitedStep >= this.#totalSteps();
      }

      /** @returns {boolean} Finalize from any step only after visiting the last step and all mins are met. */
      #canFinalizeFromPrimaryCta() {
        return this.#hasVisitedLastBuilderStep() && this.#allStepsSatisfyMin();
      }

      #onPrimaryCta() {
        const total = this.#totalSteps();

        if (this.#canFinalizeFromPrimaryCta()) {
          this.#finalizeBundleFromPrimaryCta();
          return;
        }

        if (this.#currentStep < total) {
          if (!this.#stepSatisfiesMin(this.#currentStep)) return;
          this.#setBuilderError(false);
          this.#goToStep(this.#currentStep + 1);
          return;
        }
      }

      /** Add to cart / redirect when bundle requirements are met (any step). */
      #finalizeBundleFromPrimaryCta() {
        if (this.#biscuitsBridgeActive()) {
          const atc = this.#findCbbThemeAddToCartButton();
          if (atc instanceof HTMLElement) {
            if (atc.classList.contains("biscuits--disabled")) {
              const errText = this.#findBiscuitsFormErrorMessage();
              this.#setBuilderError(
                true,
                errText || "Unable to add this bundle yet. Complete any missing selections required by the bundle form above."
              );
              return;
            }
            this.#setBuilderError(false);
            this.#delegateToBiscuitsAddToCart(atc);
            return;
          }
          this.#setBuilderError(true, "Bundle checkout is not ready. Add the “Buy buttons” block, choose a Biscuits bundle product, then try again.");
          return;
        }

        const url = this.#buildBundleUrl();
        if (!url) return;

        this.#setBuilderError(false);

        window.location.href = url;
      }

      /** @param {number} step */
      #stepperJumpAllowed(step) {
        if (step === this.#currentStep) return false;
        const total = this.#totalSteps();
        if (step < 1 || step > total) return false;

        if (step <= this.#furthestVisitedStep && step !== this.#currentStep) {
          return true;
        }

        if (step > this.#currentStep) {
          for (let s = this.#currentStep; s < step; s += 1) {
            if (!this.#stepSatisfiesMin(s)) return false;
          }
          return true;
        }

        return false;
      }

      /** @param {number} step */
      #goToStep(step) {
        const total = this.#totalSteps();
        if (step < 1 || step > total) return;

        const prevStep = this.#currentStep;

        if (step > this.#furthestVisitedStep) {
          this.#furthestVisitedStep = step;
        }
        this.#currentStep = step;

        for (let s = 1; s <= total; s += 1) {
          const panel = this.querySelector(`[data-cbb-step-panel="${s}"]`);
          if (!panel) continue;

          if (s === step) {
            panel.hidden = false;
            panel.classList.add("custom-bundle-builder-v2__step-panel--active");
          } else {
            panel.hidden = true;
            panel.classList.remove("custom-bundle-builder-v2__step-panel--active");
          }
        }

        this.#scheduleInitSwiper(step);
        this.#setBuilderError(false);
        this.#refreshUI();

        if (step !== prevStep) {
          // Collapse sticky summary so the new step grid is visible after scroll-to-top.
          this.#resetIntroAccordionPanel();
          this.#scrollBundleSectionIntoViewOnStepChange();
        }
      }

      /**
       * Scroll so the bundle section start sits below the theme sticky stack (forward, back, or stepper jump).
       */
      #scrollBundleSectionIntoViewOnStepChange() {
        const section = this.closest(".shopify-section");
        if (!(section instanceof HTMLElement)) return;

        const offset = cbbReadThemeHeaderStackPx();
        const behavior = this.#prefersReducedMotion() ? "auto" : "smooth";

        const apply = () => {
          const y =
            section.getBoundingClientRect().top + (window.scrollY || window.pageYOffset || 0) - offset;
          try {
            window.scrollTo({ top: Math.max(0, y), behavior });
          } catch (e) {
            window.scrollTo(0, Math.max(0, y));
          }
        };

        // Extra frame on narrow layouts: step panels toggle `hidden` + swiper layout; wait for geometry to settle (mobile WebKit).
        if (this.#isNarrowBundleLayout()) {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              requestAnimationFrame(apply);
            });
          });
        } else {
          requestAnimationFrame(() => {
            requestAnimationFrame(apply);
          });
        }
      }

      /** @param {number} step 1-based */
      #remainingMinPlaceholdersForStep(step) {
        const min = Number(this.#config.stepMinPicks[step - 1] ?? 0);
        if (!Number.isFinite(min) || min <= 0) return 0;
        return Math.max(0, min - this.#countForStep(step));
      }

      #remainingMinPlaceholdersForCurrentStep() {
        return this.#remainingMinPlaceholdersForStep(this.#currentStep);
      }

      /** @param {number} slotIndex */
      #requiredPickLabelText(slotIndex) {
        const step = this.#currentStep;
        const raw = String(this.#config.stepTierRequiredLabels[step - 1] || "").trim();
        if (raw) return raw.replace(/__INDEX__/g, String(slotIndex));
        return this.#slotPlaceholderForIndex(slotIndex);
      }

      #requiredPickImageUrl() {
        return String(this.#config.stepTierRequiredImageUrls[this.#currentStep - 1] || "").trim();
      }

      /** @param {number} step */
      #countForStep(step) {
        let n = 0;
        for (const entry of this.#selection.values()) {
          if (entry.stepIndex !== step) continue;
          const q = Number.isFinite(entry.quantity) && entry.quantity > 0 ? entry.quantity : 1;
          n += q;
        }
        return n;
      }

      #totalSelectedItemCount() {
        let n = 0;
        for (const entry of this.#selection.values()) {
          const q = Number.isFinite(entry.quantity) && entry.quantity > 0 ? entry.quantity : 1;
          n += q;
        }
        return n;
      }

      /** @param {number} step */
      #stepSatisfiesMin(step) {
        const min = this.#config.stepMinPicks[step - 1] ?? 0;
        return this.#countForStep(step) >= min;
      }

      #allStepsSatisfyMin() {
        for (let s = 1; s <= this.#totalSteps(); s += 1) {
          if (!this.#stepSatisfiesMin(s)) return false;
        }
        return true;
      }

      /** @param {number} count */
      #bestDiscountPercent(count) {
        let best = 0;
        const tiers = this.#config.discountTiers || [];
        for (const tier of tiers) {
          const th = tier.threshold || 0;
          const pct = tier.percent || 0;
          if (count >= th && pct > best) best = pct;
        }
        return best;
      }

      /** @returns {{ need: number; pct: number } | null} */
      #nextTierProgressMeta(selCount) {
        const tiers = this.#config.discountTiers || [];
        if (!Array.isArray(tiers) || tiers.length === 0) return null;

        const next = tiers.find((tier) => selCount < (tier.threshold ?? 0));
        if (!next) return null;

        const th = Number(next.threshold ?? 0);
        const pct = Number(next.percent ?? 0);
        if (!Number.isFinite(th) || th <= 0) return null;

        const need = Math.max(0, th - selCount);
        if (need <= 0) return null;

        return { need, pct };
      }

      #maxDiscountThreshold() {
        let m = 0;
        const tiers = this.#config.discountTiers || [];
        for (const tier of tiers) {
          const th = Number(tier.threshold ?? 0);
          if (Number.isFinite(th) && th > m) m = th;
        }
        return m;
      }

      /** @param {number} index One-based bundle position; same as Discount tier threshold index. */
      #milestonePercentAtIndex(index) {
        /** @type {number | null} */
        let best = null;
        const tiers = this.#config.discountTiers || [];
        for (const tier of tiers) {
          if (Number(tier.threshold ?? 0) !== index) continue;
          const p = Number(tier.percent ?? 0);
          if (!Number.isFinite(p)) continue;
          if (best == null || p > best) best = p;
        }
        return best;
      }

      /** @returns {string[]} Variant ids in Map order, each repeated by quantity. */
      #flattenSelectionUnits() {
        const out = [];
        for (const [key, entry] of this.#selection) {
          const q = Number.isFinite(entry.quantity) && entry.quantity > 0 ? entry.quantity : 1;
          for (let i = 0; i < q; i += 1) {
            out.push(key);
          }
        }
        return out;
      }

      /** @param {HTMLElement} card @param {{ mediaBgColor?: string; mediaBgImage?: string }} entry */
      #applyCardMediaMetaToEntry(card, entry) {
        const c = card.getAttribute("data-cbb-media-bg");
        const img = card.getAttribute("data-cbb-slot-bg-image");
        const color = c && c.trim() ? c.trim() : "";
        const image = img && img.trim() ? img.trim() : "";
        if (color) {
          entry.mediaBgColor = color;
        } else {
          delete entry.mediaBgColor;
        }
        if (image) {
          entry.mediaBgImage = image;
        } else {
          delete entry.mediaBgImage;
        }
      }

      /** @param {number} stepIndex One-based */
      #tierProgressTemplateForStep(stepIndex) {
        const arr = this.#config?.stepTierProgressTemplates;
        const fallback = "Pick __NEED__ more to get __PCT__% off";
        if (!Array.isArray(arr) || stepIndex < 1 || stepIndex > arr.length) {
          return fallback;
        }
        const raw = arr[stepIndex - 1];
        return typeof raw === "string" && raw.trim() !== "" ? raw.trim() : fallback;
      }

      /** @param {number} stepIndex One-based */
      #tierProgressSingularTemplateForStep(stepIndex) {
        const arr = this.#config?.stepTierProgressTemplatesSingular;
        if (!Array.isArray(arr) || stepIndex < 1 || stepIndex > arr.length) {
          return "";
        }
        const raw = arr[stepIndex - 1];
        return typeof raw === "string" ? raw.trim() : "";
      }

      /** @param {HTMLElement} el @param {number} stepIndex One-based */
      #fillTierProgressTitle(el, stepIndex) {
        el.textContent = "";

        const meta = this.#nextTierProgressMeta(this.#totalSelectedItemCount());
        if (!meta) return;

        let template = this.#tierProgressTemplateForStep(stepIndex);
        if (meta.need === 1) {
          const singular = this.#tierProgressSingularTemplateForStep(stepIndex);
          if (singular !== "") template = singular;
        }
        const withNeed = template.replace(/__NEED__/g, String(meta.need));
        const parts = withNeed.split("__PCT__");
        const before = parts[0] ?? "";
        const after = parts.slice(1).join("__PCT__");

        if (before) el.append(document.createTextNode(before));

        const badge = document.createElement("span");
        badge.className = "custom-bundle-builder-v2__heading__progress-badge";
        badge.textContent = `${meta.pct}%`;
        el.append(badge);

        if (after) el.append(document.createTextNode(after));
      }

      /** @param {number} index */
      #slotPlaceholderForIndex(index) {
        const tplRaw = this.#config?.slotPlaceholderTemplate;
        const tpl =
          typeof tplRaw === "string" && tplRaw.trim() !== "" ? tplRaw.trim() : "Figurine #__INDEX__";
        return tpl.replace(/__INDEX__/g, String(index));
      }

      /**
       * @param {string | undefined | null} sourceAlt
       * @param {string} fallback
       * @returns {string}
       */
      #resolveImageAlt(sourceAlt, fallback) {
        const alt = (sourceAlt || "").trim();
        return alt || fallback;
      }

      /**
       * @param {HTMLElement} track
       * @param {number} N
       * @param {number} selCount
       * @param {string[]} flat
       * @param {number} reqPad
       */
      #renderTierTrack(track, N, selCount, flat, reqPad) {
        track.textContent = "";
        const emptyImg = String(this.#config?.slotEmptyImageUrl || "").trim();

        for (let i = 1; i <= N; i += 1) {
          const ms = this.#milestonePercentAtIndex(i);
          const filled = i <= selCount;
          const variantKey = filled ? flat[i - 1] : null;
          const rawReqSlot = !filled && reqPad > 0 && i > selCount && i <= selCount + reqPad;
          let isReqSlot = rawReqSlot;
          if (isReqSlot && ms != null) {
            isReqSlot = false;
          }

          const li = document.createElement("li");
          li.className = "custom-bundle-builder-v2__tier-item";
          li.setAttribute("data-cbb-tier-slot-index", String(i));

          if (filled && variantKey) {
            const row = document.createElement("div");
            row.className = "custom-bundle-builder-v2__tier-row custom-bundle-builder-v2__tier-row--filled";
            if (ms != null) {
              row.classList.add("custom-bundle-builder-v2__tier-row--milestone");
            }

            const entry = this.#selection.get(variantKey);
            const src = (entry && entry.imageUrl) || "";
            const titleText = (entry && entry.title) || "";

            const media = document.createElement("div");
            media.className = "custom-bundle-builder-v2__tier-row__media";
            const bgColor = entry && entry.mediaBgColor ? String(entry.mediaBgColor) : "";
            const bgImage = entry && entry.mediaBgImage ? String(entry.mediaBgImage) : "";
            if (bgColor) {
              media.style.backgroundColor = bgColor;
            }
            if (bgImage) {
              const safe = bgImage.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
              media.style.backgroundImage = `url("${safe}")`;
              media.style.backgroundSize = "cover";
              media.style.backgroundPosition = "center";
              media.style.backgroundRepeat = "no-repeat";
            }
            if (src) {
              const img = document.createElement("img");
              img.className = "custom-bundle-builder-v2__tier-row__img";
              img.src = src;
              img.alt = this.#resolveImageAlt(titleText || entry?.imageAlt, `Bundle item ${i}`);
              media.append(img);
            }

            const body = document.createElement("div");
            body.className = "custom-bundle-builder-v2__tier-row__body";
            const titleEl = document.createElement("span");
            titleEl.className = "custom-bundle-builder-v2__tier-row__title";
            titleEl.textContent = titleText;
            const priceEl = document.createElement("span");
            priceEl.className = "custom-bundle-builder-v2__tier-row__price";
            const unitCents = entry && Number.isFinite(entry.priceCents) ? entry.priceCents : 0;
            priceEl.textContent = this.#formatMoneyCents(unitCents);
            body.append(titleEl, priceEl);

            const noticeCfg = this.#config?.deliveryDelayNotice;
            const delayMap = this.#config?.deliveryDelayMap || {};
            const entryVariantId = variantKey != null ? String(variantKey) : null;
            const delayData = entryVariantId ? delayMap[entryVariantId] : null;
            const DD = window.DeliveryDelay;
            const resolvedDelay = DD ? DD.resolveItem(delayData) : delayData;
            if (noticeCfg && resolvedDelay) {
              const i18n = this.#config?.deliveryDelayI18n || {};
              const dateShort = DD
                ? DD.formatDate(resolvedDelay.shipFrom, i18n.dateFormatShort || "%b %-d")
                : resolvedDelay.shipFrom;
              const labelText = DD
                ? DD.interpolate(i18n.badgeShort || "Ships from {{ date_short }}", {
                  date_short: dateShort,
                })
                : dateShort;
              if (String(labelText || "").trim() !== "") {
                const labelEl = document.createElement("span");
                labelEl.className = "delivery-delay-tier-label";
                labelEl.textContent = String(labelText).trim();
                if (String(noticeCfg.tierRowLabelColor || "").trim() !== "") {
                  labelEl.style.color = String(noticeCfg.tierRowLabelColor).trim();
                }
                body.append(labelEl);
              }
            }

            const idxBadge = document.createElement("span");
            idxBadge.className = "custom-bundle-builder-v2__tier-row__index";
            idxBadge.textContent = String(i);

            const rm = document.createElement("button");
            rm.type = "button";
            rm.className = "custom-bundle-builder-v2__tier-row__remove";
            rm.setAttribute("data-cbb-tier-remove", "");
            rm.setAttribute("data-cbb-tier-slot-index", String(i));
            rm.setAttribute("aria-label", `Remove item ${i} from bundle`);
            rm.innerHTML =
              '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M4 7h16M10 11v6M14 11v6M6 7l1 12a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-12M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"/></svg>';

            row.append(media, body, idxBadge);
            if (ms != null) {
              const pctEl = document.createElement("span");
              pctEl.className = "custom-bundle-builder-v2__tier-row__milestone-pct";
              pctEl.textContent = `-${ms}%`;
              row.append(pctEl);
            }
            row.append(rm);

            li.append(row);
            track.append(li);
            continue;
          }

          const row = document.createElement("div");
          row.className = "custom-bundle-builder-v2__tier-row custom-bundle-builder-v2__tier-row--empty";
          if (isReqSlot) {
            row.classList.add("custom-bundle-builder-v2__tier-row--required-pick");
          }
          if (ms != null) {
            row.classList.add("custom-bundle-builder-v2__tier-row--milestone");
          }

          const media = document.createElement("div");
          media.className =
            "custom-bundle-builder-v2__tier-row__media custom-bundle-builder-v2__tier-row__media--placeholder";
          if (ms != null) {
            const pct = document.createElement("span");
            pct.className = "custom-bundle-builder-v2__tier-row__placeholder-pct";
            pct.textContent = `-${ms}%`;
            media.append(pct);
          } else if (isReqSlot) {
            const reqImg = this.#requiredPickImageUrl();
            if (reqImg !== "") {
              const img = document.createElement("img");
              img.className = "custom-bundle-builder-v2__tier-row__placeholder-img";
              img.src = reqImg;
              img.alt = this.#resolveImageAlt("", "Required pick placeholder");
              media.append(img);
            } else if (emptyImg !== "") {
              const img = document.createElement("img");
              img.className = "custom-bundle-builder-v2__tier-row__placeholder-img";
              img.src = emptyImg;
              img.alt = this.#resolveImageAlt("", `Bundle slot ${i}`);
              media.append(img);
            }
          } else if (emptyImg !== "") {
            const img = document.createElement("img");
            img.className = "custom-bundle-builder-v2__tier-row__placeholder-img";
            img.src = emptyImg;
            img.alt = this.#resolveImageAlt("", `Bundle slot ${i}`);
            media.append(img);
          }

          const ph = document.createElement("span");
          ph.className = "custom-bundle-builder-v2__tier-row__placeholder-label";
          ph.textContent = rawReqSlot ? this.#requiredPickLabelText(i) : this.#slotPlaceholderForIndex(i);

          row.append(media, ph);

          li.append(row);
          track.append(li);
        }
      }

      #updateTierStrip() {
        const wrap = this.querySelector("[data-cbb-tier]");
        const track = this.querySelector("[data-cbb-tier-track]");
        const scrollHost = this.querySelector("[data-cbb-tier-track-wrap]");

        if (!(wrap instanceof HTMLElement) || !(track instanceof HTMLElement)) {
          return;
        }

        const selCount = this.#totalSelectedItemCount();
        const maxThr = this.#maxDiscountThreshold();
        const reqPad = this.#remainingMinPlaceholdersForCurrentStep();
        const showTier = maxThr > 0 || selCount > 0 || reqPad > 0;

        if (!showTier) {
          wrap.hidden = true;
          track.textContent = "";
          this.querySelectorAll("[data-cbb-step-heading-progress]").forEach((node) => {
            if (node instanceof HTMLElement) node.textContent = "";
          });
          return;
        }

        wrap.hidden = false;

        this.querySelectorAll("[data-cbb-step-heading-progress]").forEach((node) => {
          if (!(node instanceof HTMLElement)) return;
          const panel = node.closest("[data-cbb-step-panel]");
          const stepIdx = panel ? parseInt(panel.getAttribute("data-cbb-step-panel") || "0", 10) : 0;
          if (stepIdx === this.#currentStep) {
            this.#fillTierProgressTitle(node, stepIdx);
          } else {
            node.textContent = "";
          }
        });

        const N = Math.max(maxThr, selCount + reqPad);
        const flat = this.#flattenSelectionUnits();
        const shouldScroll = this.#tierScrollPending;
        this.#tierScrollPending = false;

        this.#renderTierTrack(track, N, selCount, flat, reqPad);

        if (shouldScroll && selCount > 0 && scrollHost instanceof HTMLElement) {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              const cell = track.querySelector(`[data-cbb-tier-slot-index="${selCount}"]`);
              if (cell instanceof HTMLElement) {
                cell.scrollIntoView({ block: "nearest", inline: "end", behavior: "smooth" });
              }
            });
          });
        }
      }

      /** @param {string} key */
      #decrementSelectionVariantKey(key) {
        const entry = this.#selection.get(key);
        if (!entry) return;

        const qty = Number.isFinite(entry.quantity) && entry.quantity > 0 ? entry.quantity : 1;
        const esc =
          typeof CSS !== "undefined" && typeof CSS.escape === "function"
            ? CSS.escape(key)
            : key.replace(/["\\]/g, "\\$&");
        const card = this.querySelector(`[data-cbb-product-card][data-variant-id="${esc}"]`);

        if (qty <= 1) {
          this.#selection.delete(key);
          if (card instanceof HTMLElement) {
            card.classList.remove("custom-bundle-builder-v2__card--selected");
            const addBtn = card.querySelector("[data-cbb-add]");
            if (addBtn instanceof HTMLElement) {
              addBtn.setAttribute("aria-pressed", "false");
            }
          }
        } else {
          entry.quantity = qty - 1;
        }
      }

      /** @param {number} slotIndex One-based tier tile index (matches bundle position). */
      #onTierFilledCellClick(slotIndex) {
        const selCount = this.#totalSelectedItemCount();
        if (slotIndex < 1 || slotIndex > selCount) return;

        const flat = this.#flattenSelectionUnits();
        const keyTarget = flat[slotIndex - 1];
        if (!keyTarget) return;

        const esc =
          typeof CSS !== "undefined" && typeof CSS.escape === "function"
            ? CSS.escape(keyTarget)
            : keyTarget.replace(/["\\]/g, "\\$&");
        const card = this.querySelector(`[data-cbb-product-card][data-variant-id="${esc}"]`);

        if (this.#biscuitsBridgeActive()) {
          if (!(card instanceof HTMLElement)) return;
          if (!this.#bridgeClickBiscuitsQuantity(card, "minus")) return;
        }

        const priorStep = this.#currentStep;
        this.#decrementSelectionVariantKey(keyTarget);

        if (this.#selection.size === 0) {
          this.#tierScrollPending = true;
          this.#setBuilderError(false);
          this.#finalizeEmptyBundleNavigation(priorStep);
          return;
        }
        this.#refreshUI();
      }

      #clearEntireSelection() {
        const stepBeforeClear = this.#currentStep;
        while (this.#selection.size > 0) {
          const keys = [...this.#selection.keys()];
          const variantId = keys[0];
          if (!variantId) break;

          const esc =
            typeof CSS !== "undefined" && typeof CSS.escape === "function"
              ? CSS.escape(variantId)
              : variantId.replace(/["\\]/g, "\\$&");
          const card = this.querySelector(`[data-cbb-product-card][data-variant-id="${esc}"]`);

          if (this.#biscuitsBridgeActive()) {
            if (!(card instanceof HTMLElement)) break;
            if (!this.#bridgeClickBiscuitsQuantity(card, "minus")) break;
          }

          this.#decrementSelectionVariantKey(variantId);
        }

        this.querySelectorAll("[data-cbb-product-card]").forEach((card) => {
          if (!(card instanceof HTMLElement)) return;
          card.classList.remove("custom-bundle-builder-v2__card--selected");
          const addBtn = card.querySelector("[data-cbb-add]");
          if (addBtn instanceof HTMLElement) {
            addBtn.setAttribute("aria-pressed", "false");
          }
        });

        this.#tierScrollPending = true;
        this.#setBuilderError(false);
        if (this.#selection.size === 0) {
          this.#finalizeEmptyBundleNavigation(stepBeforeClear);
        } else {
          this.#refreshUI();
        }
      }

      #updateBackCta() {
        const btn = this.querySelector("[data-cbb-back-cta]");
        if (!(btn instanceof HTMLButtonElement)) return;
        btn.disabled = this.#currentStep <= 1;
      }

      #updateRemoveAllState() {
        this.querySelectorAll("[data-cbb-remove-all]").forEach((b) => {
          if (b instanceof HTMLButtonElement) {
            b.toggleAttribute("disabled", this.#selection.size === 0);
          }
        });
      }

      #stripMoneyHtml(raw) {
        if (raw == null) return "";

        let s = String(raw).trim();
        if (s === "") return "";

        if (!s.includes("<")) return s;

        if (typeof document !== "undefined" && document.createElement) {
          const tmp = document.createElement("template");
          tmp.innerHTML = s;

          let text = tmp.content.textContent || tmp.content.innerText || "";

          text = text.replace(/\s+/g, " ").trim();

          return text !== "" ? text : s.replace(/<[^>]+>/g, "").replace(/\s+/g, " ").trim();
        }

        return s.replace(/<[^>]+>/g, "").replace(/\s+/g, " ").trim();
      }

      #formatMoneyCents(cents) {
        const num = Number(cents);
        /** @type {string} */

        try {
          if (typeof window.formatMoney === "function") {
            return this.#stripMoneyHtml(window.formatMoney(num));
          }
        } catch (e) {
          /* fall through */
        }

        const safe = Number.isFinite(num) ? num : 0;

        return this.#stripMoneyHtml((safe / 100).toFixed(2));
      }

      #buildSummaryCountText(selCount) {
        let t = (this.#config.summaryCountTemplate || "").replace(/__COUNT__/g, String(selCount));
        t = t.replace(/\s*\/\s*__MAX__/g, "").replace(/__MAX__/g, "").replace(/\s{2,}/g, " ").trim();
        return t;
      }

      #buildBundleUrl() {
        const base = (this.dataset.bundleBaseUrl || "").trim();
        if (!base || this.#selection.size === 0) return "";

        const params = [...this.#selection.entries()]
          .map(([id, row]) => {
            const q = Number.isFinite(row.quantity) && row.quantity > 0 ? row.quantity : 1;
            return `${encodeURIComponent(id)}%3A${encodeURIComponent(String(q))}`;
          })
          .join("%2C");

        let url = base.includes("?") ? `${base}&bundle=${params}` : `${base}?bundle=${params}`;

        return url;
      }

      #bundleLinkSharingEnabled() {
        return this.dataset.enableBundleLinkSharing === "true";
      }

      /** @returns {{ label: string; selectProducts: string; copying: string; copied: string; error: string }} */
      #bundleShareLabels() {
        return {
          label: (this.dataset.shareLinkLabel || "Copy bundle link").trim(),
          selectProducts: (this.dataset.shareLinkSelect || "Please select products").trim(),
          copying: (this.dataset.shareLinkCopying || "Copying...").trim(),
          copied: (this.dataset.shareLinkCopied || "Copied!").trim(),
          error: (this.dataset.shareLinkError || "Copy failed").trim(),
        };
      }

      #buildShareableBundleUrl() {
        if (typeof window === "undefined" || this.#selection.size === 0) return "";

        try {
          const url = new URL(window.location.href);
          url.searchParams.delete("bundle");

          const bundleData = [...this.#selection.entries()]
            .map(([id, row]) => {
              const q = Number.isFinite(row.quantity) && row.quantity > 0 ? row.quantity : 1;
              return `${id}:${q}`;
            })
            .join(",");

          url.searchParams.set("bundle", bundleData);
          return url.toString();
        } catch (e) {
          return "";
        }
      }

      #syncBrowserBundleUrl() {
        if (!this.#bundleLinkSharingEnabled()) return;
        if (typeof window === "undefined" || typeof window.history?.replaceState !== "function") return;

        if (this.#bundleUrlUpdateTimer != null) {
          clearTimeout(this.#bundleUrlUpdateTimer);
        }

        this.#bundleUrlUpdateTimer = window.setTimeout(() => {
          this.#bundleUrlUpdateTimer = null;

          try {
            const url = new URL(window.location.href);
            url.searchParams.delete("bundle");

            if (this.#selection.size > 0) {
              const bundleData = [...this.#selection.entries()]
                .map(([id, row]) => {
                  const q = Number.isFinite(row.quantity) && row.quantity > 0 ? row.quantity : 1;
                  return `${id}:${q}`;
                })
                .join(",");
              url.searchParams.set("bundle", bundleData);
            }

            const next = url.toString();
            if (next !== window.location.href) {
              window.history.replaceState({}, "", next);
            }
          } catch (e) {
            /* URL sync is best-effort */
          }
        }, 300);
      }

      /** Copy the current page URL with ?bundle= selections to the clipboard. */
      async #copyBundleLink() {
        const labels = this.#bundleShareLabels();

        if (this.#selection.size === 0) {
          this.#showCopyBundleLinkStatus(labels.selectProducts);
          return;
        }

        const url = this.#buildShareableBundleUrl();
        if (!url) {
          this.#showCopyBundleLinkStatus(labels.error);
          return;
        }

        this.#showCopyBundleLinkStatus(labels.copying);

        try {
          await navigator.clipboard.writeText(url);
          this.#showCopyBundleLinkStatus(labels.copied);
          return;
        } catch (e) {
          /* fall through to legacy copy */
        }

        try {
          const ta = document.createElement("textarea");
          ta.value = url;
          ta.setAttribute("readonly", "");
          ta.style.position = "fixed";
          ta.style.left = "-9999px";
          document.body.appendChild(ta);
          ta.select();
          document.execCommand("copy");
          document.body.removeChild(ta);
          this.#showCopyBundleLinkStatus(labels.copied);
        } catch (e) {
          this.#showCopyBundleLinkStatus(labels.error);
        }
      }

      /** @param {string} message */
      #showCopyBundleLinkStatus(message) {
        const labels = this.#bundleShareLabels();

        if (this.#copyBundleLinkStatusTimer != null) {
          clearTimeout(this.#copyBundleLinkStatusTimer);
          this.#copyBundleLinkStatusTimer = null;
        }

        this.querySelectorAll("[data-cbb-copy-bundle-link]").forEach((btn) => {
          if (!(btn instanceof HTMLButtonElement)) return;
          if (!btn.dataset.cbbShareLinkDefaultLabel) {
            btn.dataset.cbbShareLinkDefaultLabel = labels.label;
          }
          btn.textContent = message;
        });

        this.#copyBundleLinkStatusTimer = window.setTimeout(() => {
          this.#copyBundleLinkStatusTimer = null;
          this.querySelectorAll("[data-cbb-copy-bundle-link]").forEach((btn) => {
            if (!(btn instanceof HTMLButtonElement)) return;
            btn.textContent = btn.dataset.cbbShareLinkDefaultLabel || labels.label;
          });
        }, 3000);
      }

      /** Sets `#furthestVisitedStep` from `max(selection.stepIndex)` (clamped); used for restores/URL bundles with optional steps whose min picks are zero. */
      #syncFurthestVisitedFromHighestSelectedStep() {
        const total = this.#totalSteps();
        if (total <= 0 || this.#selection.size === 0) return;

        let maxSi = 0;
        for (const row of this.#selection.values()) {
          const si = row.stepIndex;
          if (!Number.isFinite(si)) continue;
          if (si > maxSi) maxSi = si;
        }
        if (maxSi <= 0) return;
        this.#furthestVisitedStep = Math.min(total, Math.max(1, Math.floor(maxSi)));
      }

      /**
       * When the bundle has no variants (Remove-all parity), reset step scope and Furthest-tracking.
       * @param {number} stepBeforeClear The active step immediately before `#goToStep(1)`.
       */
      #finalizeEmptyBundleNavigation(stepBeforeClear) {
        this.#furthestVisitedStep = 1;
        this.#goToStep(1);
        if (stepBeforeClear === 1) {
          this.#scrollBundleSectionIntoViewOnStepChange();
        }
      }

      #refreshUI() {
        this.#updateSummary();
        this.#updateDeliveryDelayNotice();
        this.#updateStepper();
        this.#updateCta();
        this.#updateRemoveAllState();
        this.#syncCardsWithSelection();
        this.#updateTierStrip();
        this.#scheduleIntroActionsSlotStuckUpdate();
        this.#schedulePersistSelectionToStorage();
        this.#syncBrowserBundleUrl();
        this.dispatchEvent(new CustomEvent("cbb:ui-refreshed", { bubbles: false }));
      }

      #syncCardsWithSelection() {
        this.querySelectorAll("[data-cbb-product-card]").forEach((card) => {
          const id = card.getAttribute("data-variant-id");
          const btn = card.querySelector("[data-cbb-add]");
          if (!id || !btn) return;
          const on = this.#selection.has(String(id));
          card.classList.toggle("custom-bundle-builder-v2__card--selected", on);
          btn.setAttribute("aria-pressed", on ? "true" : "false");
          const wrap = card.querySelector("[data-cbb-qty-wrap]");
          const valEl = card.querySelector("[data-cbb-qty-value]");
          const minusBtn = card.querySelector("[data-cbb-qty-minus]");
          const plusBtn = card.querySelector("[data-cbb-qty-plus]");
          const row = this.#selection.get(String(id));
          const q = row && Number.isFinite(row.quantity) && row.quantity > 0 ? row.quantity : 1;
          const alwaysQty = this.#qtySelectorAlwaysVisible();
          if (wrap instanceof HTMLElement) {
            wrap.hidden = !alwaysQty && !on;
          }
          if (valEl instanceof HTMLElement) {
            valEl.textContent = on ? String(q) : "0";
          }

          const qtyIdle = alwaysQty && !on;
          for (const b of [minusBtn, plusBtn]) {
            if (!(b instanceof HTMLButtonElement)) continue;
            b.disabled = Boolean(qtyIdle);
          }

          const imgEl = card.querySelector(".custom-bundle-builder-v2__card-img");
          if (row && imgEl instanceof HTMLImageElement) {
            const u = (imgEl.currentSrc || imgEl.src || "").trim();
            if (u) row.imageUrl = u;
            const alt = (imgEl.alt || "").trim();
            if (alt) row.imageAlt = alt;
          }
          const link = card.querySelector("[data-cbb-product-link]");
          const titleText = link
            ? (link.textContent || "").replace(/\s+/g, " ").trim()
            : (card.querySelector(".custom-bundle-builder-v2__card-title")?.textContent || "")
              .replace(/\s+/g, " ")
              .trim();
          if (row && titleText) row.title = titleText;
          if (row && card instanceof HTMLElement) {
            const productIdRaw = card.getAttribute("data-product-id");
            const productId = productIdRaw ? Number(productIdRaw) : NaN;
            if (Number.isFinite(productId)) {
              row.productId = productId;
            }
            this.#applyCardMediaMetaToEntry(card, row);
          }
        });
      }

      #getDeliveryDelayGroup() {
        const delayMap = this.#config?.deliveryDelayMap || {};
        const components = [];

        for (const [variantId, entry] of this.#selection) {
          const qty = Number.isFinite(entry?.quantity) && entry.quantity > 0 ? entry.quantity : 1;
          const delayData = delayMap[String(variantId)] || null;
          for (let i = 0; i < qty; i += 1) {
            components.push(delayData);
          }
        }

        if (window.DeliveryDelay) {
          return window.DeliveryDelay.resolveGroup(components);
        }

        const delayed = components.filter(Boolean);
        if (delayed.length === 0) return { state: "NONE", names: [], count: 0 };
        return {
          state: delayed.length === components.length ? "FULL" : "PARTIAL",
          shipFrom: delayed.map((d) => d.shipFrom).sort().slice(-1)[0],
          names: delayed.map((d) => d.name).filter(Boolean),
          count: delayed.length,
        };
      }

      #countDeliveryDelayItems() {
        return this.#getDeliveryDelayGroup().count || 0;
      }

      /** @returns {{ name: string, value: string } | null} */
      #getActiveDeliveryDelayProperty() {
        const notice = this.querySelector("[data-cbb-delivery-delay-notice]");
        const optionsRoot = notice?.querySelector("[data-delivery-delay-options]");
        if (!(optionsRoot instanceof HTMLElement)) return null;

        const selected = optionsRoot.querySelector('input[type="radio"]:checked');
        const defaultChoice =
          (optionsRoot.getAttribute("data-delivery-delay-default-choice") || "primary").trim() ===
          "alternative"
            ? "alternative"
            : "primary";
        const choice = selected instanceof HTMLInputElement ? selected.value : defaultChoice;

        const primaryName = (optionsRoot.getAttribute("data-delivery-delay-primary-name") || "").trim();
        const primaryValue = (optionsRoot.getAttribute("data-delivery-delay-primary-value") || "").trim();
        const altValue = (optionsRoot.getAttribute("data-delivery-delay-alt-value") || "").trim();

        if (choice === "alternative" && altValue && primaryName) {
          return { name: primaryName, value: altValue };
        }

        if (primaryName && primaryValue) {
          return { name: primaryName, value: primaryValue };
        }

        return null;
      }

      /** @returns {{ name: string, value: string } | null} */
      #deliveryDelayPropertyConfig() {
        const fromOptions = this.#getActiveDeliveryDelayProperty();
        if (fromOptions) return fromOptions;

        const group = this.#getDeliveryDelayGroup();
        if (!group || group.state === "NONE" || !group.shipFrom) return null;

        let name =
          typeof this.#config?.deliveryDelayPropertyName === "string"
            ? this.#config.deliveryDelayPropertyName.trim()
            : "";
        const i18n = this.#config?.deliveryDelayI18n || {};
        const deliveryDelay = window.themeVariables?.settings?.deliveryDelay;

        if (!name) {
          name = String(deliveryDelay?.propertyName || "").trim();
        }
        if (!name) name = String(i18n.propertyName || "").trim();

        const dateFormatLong = i18n.dateFormatLong || deliveryDelay?.i18n?.dateFormatLong || "%B %-d, %Y";
        const lineTemplate = i18n.lineValue || deliveryDelay?.i18n?.lineValue || "Ships from {{ date }}";
        const DD = window.DeliveryDelay;
        const dateLong = DD ? DD.formatDate(group.shipFrom, dateFormatLong) : group.shipFrom;
        const value = DD ? DD.interpolate(lineTemplate, { date: dateLong }) : dateLong;

        if (!name || !value) return null;
        return { name, value };
      }

      #syncDeliveryDelayPropertyToBundleForm() {
        const atc = this.#findCbbThemeAddToCartButton();
        const form = atc instanceof HTMLElement ? atc.closest("form") : null;
        if (!(form instanceof HTMLFormElement)) return;

        form.querySelector("[data-delivery-delay-property]")?.remove();

        const existing = form.querySelector("[data-cbb-delivery-delay-property]");
        const shouldHave = this.#countDeliveryDelayItems() > 0;
        const cfg = this.#deliveryDelayPropertyConfig();
        const DD = window.DeliveryDelay;

        if (!shouldHave || !cfg) {
          existing?.remove();
          if (DD && typeof DD.syncBundleOrderAttributes === "function") {
            DD.syncBundleOrderAttributes(form, null);
          }
          return;
        }

        let input = existing;
        if (!(input instanceof HTMLInputElement)) {
          input = document.createElement("input");
          input.type = "hidden";
          input.setAttribute("data-cbb-delivery-delay-property", "");
          form.appendChild(input);
        }

        input.name = `properties[${cfg.name}]`;
        input.value = cfg.value;

        // Order-level attributes survive Biscuits/Cart Transform expand (line properties do not).
        if (DD && typeof DD.syncBundleOrderAttributes === "function") {
          const group = this.#getDeliveryDelayGroup();
          const items =
            typeof DD.formatItemsAttribute === "function"
              ? DD.formatItemsAttribute(group.names || [])
              : (group.names || []).filter(Boolean).join(", ");
          DD.syncBundleOrderAttributes(form, {
            name: cfg.name,
            value: cfg.value,
            items,
          });
        }
      }

      #updateDeliveryDelayNotice() {
        const noticeCfg = this.#config?.deliveryDelayNotice;
        const notices = this.querySelectorAll("[data-cbb-delivery-delay-notice]");
        if (!notices.length) return;

        const group = this.#getDeliveryDelayGroup();
        const show = noticeCfg != null && group.state !== "NONE";
        const i18n = this.#config?.deliveryDelayI18n || {};
        const DD = window.DeliveryDelay;
        if (!DD) {
          for (const noticeEl of notices) {
            if (noticeEl instanceof HTMLElement) noticeEl.hidden = true;
          }
          this.#syncDeliveryDelayPropertyToBundleForm();
          return;
        }

        const dateLong = group.shipFrom
          ? DD.formatDate(group.shipFrom, i18n.dateFormatLong || "%B %-d, %Y")
          : "";
        const names = DD.formatNames(group.names || [], i18n.listAnd || "and");
        const count = group.count || 0;

        let titleText = "";
        let bodyText = "";
        if (group.state === "PARTIAL") {
          const titleTpl = count === 1 ? i18n.partialTitleOne : i18n.partialTitleOther;
          const bodyTpl = count === 1 ? i18n.partialBodyOne : i18n.partialBodyOther;
          titleText = DD.interpolate(titleTpl || "", { count, names, date: dateLong });
          bodyText = DD.interpolate(bodyTpl || "", { count, names, date: dateLong });
        } else if (group.state === "FULL") {
          titleText = "";
          bodyText = DD.interpolate(i18n.fullBody || "", { count, names, date: dateLong });
        }

        const lineValue = group.shipFrom
          ? DD.interpolate(i18n.lineValue || "Ships from {{ date }}", { date: dateLong })
          : "";

        for (const noticeEl of notices) {
          if (!(noticeEl instanceof HTMLElement)) continue;

          if (!show) {
            noticeEl.hidden = true;
            continue;
          }

          noticeEl.hidden = false;

          const titleEl = noticeEl.querySelector("[data-cbb-delivery-delay-notice-title]");
          const textEl = noticeEl.querySelector("[data-cbb-delivery-delay-notice-text]");
          const hasVisibleContent = titleText !== "" || bodyText !== "";

          if (!hasVisibleContent) {
            noticeEl.hidden = true;
            continue;
          }

          if (titleEl instanceof HTMLElement) {
            titleEl.textContent = titleText;
            titleEl.hidden = titleText === "";
          }

          if (textEl instanceof HTMLElement) {
            textEl.textContent = bodyText;
            textEl.hidden = bodyText === "";
          }

          const optionsRoot = noticeEl.querySelector("[data-delivery-delay-options]");
          const shippingOptions = noticeCfg?.shippingOptions;
          if (
            optionsRoot instanceof HTMLElement &&
            shippingOptions &&
            shippingOptions.enabled === true
          ) {
            const optionPartialProperty = DD.interpolate(
              shippingOptions.optionPartialProperty || shippingOptions.optionPartial || "",
              { date: dateLong },
            );
            if (optionPartialProperty || lineValue) {
              optionsRoot.setAttribute(
                "data-delivery-delay-primary-value",
                optionPartialProperty || lineValue,
              );
            }
            if (lineValue) {
              optionsRoot.setAttribute("data-delivery-delay-alt-value", lineValue);
            }

            const option1Label = optionsRoot.querySelector("[data-delivery-delay-option-1-label]");
            if (option1Label instanceof HTMLElement && shippingOptions.optionPartial) {
              option1Label.textContent = String(shippingOptions.optionPartial);
            }

            const option2Label = optionsRoot.querySelector("[data-delivery-delay-option-2-label]");
            if (option2Label instanceof HTMLElement) {
              const optionFullLabel = DD.interpolate(shippingOptions.optionFull || "", {
                date: dateLong,
              });
              if (optionFullLabel) option2Label.textContent = optionFullLabel;
            }

            if (typeof window.initDeliveryDelayNoticeOptions === "function") {
              window.initDeliveryDelayNoticeOptions(optionsRoot);
            }
          }
        }

        this.#syncDeliveryDelayPropertyToBundleForm();
      }

      #updateSummary() {
        const selCount = this.#totalSelectedItemCount();
        let retail = 0;

        for (const entry of this.#selection.values()) {
          const q = Number.isFinite(entry.quantity) && entry.quantity > 0 ? entry.quantity : 1;
          retail += entry.priceCents * q;
        }

        const pct = this.#bestDiscountPercent(selCount);
        const bundlePrice = Math.round(retail * (1 - pct / 100));

        this.dataset.cbbSelectionRetailCents = String(retail);
        this.dataset.cbbSelectionBundleCents = String(bundlePrice);

        const roots = this.querySelectorAll("[data-cbb-summary-sync-root]");
        if (!roots.length) return;

        const strike = String(this.dataset.summaryStrikethrough || "") === "true";

        const retailDisplay = this.#formatMoneyCents(retail);
        const strikeOn = strike && pct > 0 && retail > bundlePrice;

        const saveCents = Math.max(0, retail - bundlePrice);
        const saveText =
          selCount > 0 && saveCents > 0 ? this.#formatMoneyCents(saveCents) : "";
        const showSaveCol = saveText.trim() !== "";

        roots.forEach((root) => {
          if (!(root instanceof HTMLElement)) return;

          const titlePlain = String(this.#config.summaryTitle || "").trim();
          root.querySelectorAll("[data-cbb-summary-heading-text]").forEach((titleEl) => {
            if (!(titleEl instanceof HTMLElement)) return;
            titleEl.textContent = titlePlain;
            const titleRow = titleEl.closest(".custom-bundle-builder-v2__intro-card__title");
            if (titleRow instanceof HTMLElement) {
              titleRow.hidden = titlePlain === "";
            }
          });
          root.querySelectorAll("[data-cbb-summary-heading-count]").forEach((countEl) => {
            if (!(countEl instanceof HTMLElement)) return;
            const countPart = selCount > 0 ? this.#buildSummaryCountText(selCount) : "";
            countEl.textContent = countPart;
            countEl.hidden = countPart === "";
          });
          const retailEl = root.querySelector("[data-cbb-retail]");
          const totalEl = root.querySelector("[data-cbb-summary-total]");
          const saveEl = root.querySelector("[data-cbb-summary-save]");

          if (!totalEl || !saveEl) return;

          const showInlineRetail = retail > 0 && pct > 0 && strike;
          if (retailEl instanceof HTMLElement) {
            retailEl.textContent = showInlineRetail ? retailDisplay : "";
            retailEl.classList.toggle("is-strike", strikeOn);
            retailEl.hidden = !showInlineRetail;
          }

          totalEl.textContent = this.#formatMoneyCents(bundlePrice);

          saveEl.textContent = saveText;
          const saveLineHidden = !showSaveCol;
          root.querySelectorAll("[data-cbb-summary-save-wrap]").forEach((wrap) => {
            if (!(wrap instanceof HTMLElement)) return;
            wrap.hidden = saveLineHidden;
            wrap.setAttribute("aria-hidden", saveLineHidden ? "true" : "false");
          });

        });
      }

      #updateStepper() {
        const cur = this.#currentStep;
        const finalizePhaseVisited = this.#hasVisitedLastBuilderStep();

        this.querySelectorAll("[data-cbb-stepper-index]").forEach((el) => {
          const idx = parseInt(el.getAttribute("data-cbb-stepper-index") || "0", 10);

          if (this.dataset.stepperIllustrated === "true") {
            let illustratedState = "upcoming";
            if (idx === cur) illustratedState = "current";
            else if (idx < cur) illustratedState = "passed";
            el.dataset.cbbIllustrated = illustratedState;
          }

          // Matching primary CTA: once the visitor has reached the last step ("Create bundle" on every
          // panel), stepper badges are all "completed" (active/next pending styling is suppressed).
          if (finalizePhaseVisited) {
            el.dataset.cbbState = "completed";
            return;
          }

          if (idx === cur) {
            el.dataset.cbbState = "active";
          } else if (idx < cur) {
            el.dataset.cbbState = this.#stepSatisfiesMin(idx) ? "completed" : "pending";
          } else if (idx > cur && this.#stepperJumpAllowed(idx)) {
            el.dataset.cbbState = "next";
          } else {
            el.dataset.cbbState = "pending";
          }
        });

        this.querySelectorAll("[data-cbb-stepper-jump]").forEach((jumpEl) => {
          if (!(jumpEl instanceof HTMLElement)) return;
          const idx = parseInt(jumpEl.getAttribute("data-cbb-stepper-jump") || "0", 10);
          const canJump = this.#stepperJumpAllowed(idx);

          jumpEl.setAttribute("aria-disabled", canJump ? "false" : "true");
          jumpEl.setAttribute("tabindex", canJump ? "0" : "-1");
        });

        this.#syncStepperProgressFill();
        this.#applyStepperLayout();
      }

      /** Progress: 0% on step 1, 100% on last step (linear between). */
      #syncStepperProgressFill() {
        const fill = this.querySelector("[data-cbb-stepper-progress-fill]");
        const bar = this.querySelector("[data-cbb-stepper-progress]");
        if (!(fill instanceof HTMLElement)) return;

        const total = this.#totalSteps();
        const cur = this.#currentStep;
        let pct = 0;
        if (total > 1) {
          pct = ((cur - 1) / (total - 1)) * 100;
        }
        fill.style.width = `${pct}%`;

        if (bar instanceof HTMLElement) {
          const rounded = Math.max(0, Math.min(100, Math.round(pct)));
          bar.setAttribute("aria-valuenow", String(rounded));
        }
      }

      /** Reorders stepper flex items when stacked layout is active (viewport <=989px). */
      #applyStepperLayout() {
        const grow = this.querySelector("[data-cbb-stepper-mobile-grow]");
        if (!(grow instanceof HTMLElement)) {
          for (const el of this.querySelectorAll("[data-cbb-stepper-index]")) {
            if (el instanceof HTMLElement) el.style.removeProperty("order");
          }
          return;
        }

        const mq =
          typeof window !== "undefined" && typeof window.matchMedia === "function"
            ? window.matchMedia("(max-width: 989px)")
            : null;

        const mobile = Boolean(mq && mq.matches);

        for (const el of this.querySelectorAll("[data-cbb-stepper-index]")) {
          if (!(el instanceof HTMLElement)) continue;
          const idx = parseInt(el.getAttribute("data-cbb-stepper-index") || "0", 10) || 0;
          if (!mobile) el.style.removeProperty("order");
          else if (String(el.dataset.cbbState || "") === "active") el.style.order = "-100";
          else if (String(el.dataset.cbbState || "") === "completed" && idx === this.#currentStep) {
            // Finalize-phase: every badge is completed; keep the viewed step surfaced like "active".
            el.style.order = "-100";
          } else el.style.order = String(idx);
        }

        if (!mobile) {
          grow.style.removeProperty("order");
          grow.style.display = "";
          grow.setAttribute("hidden", "");
          return;
        }

        grow.removeAttribute("hidden");
        grow.style.display = "";
        grow.style.order = "-50";
      }

      #updateCta() {
        const btn = this.querySelector("[data-cbb-primary-cta]");
        const label = btn?.querySelector("[data-cbb-cta-label]");
        if (!btn || !label) return;

        const total = this.#totalSteps();
        const isLast = this.#currentStep >= total;
        const visitedLast = this.#hasVisitedLastBuilderStep();
        const allMinsOk = this.#allStepsSatisfyMin();
        const canFinalize = visitedLast && allMinsOk;

        const showFinalizeLabel = visitedLast;

        const fallbackContinue = this.dataset.continueLabel || "Continue";
        let continueText = fallbackContinue;
        if (this.#continueUsesNextStepTitle() && !showFinalizeLabel) {
          const nextTitle = this.#nextStepTitleText();
          if (nextTitle !== "") continueText = nextTitle;
        }

        label.textContent = showFinalizeLabel ? this.dataset.finalizeLabel || "Create bundle" : continueText;

        if (!(btn instanceof HTMLElement)) return;

        if (!showFinalizeLabel && this.#continueUsesNextStepTitle() && continueText !== fallbackContinue) {
          btn.setAttribute("aria-label", `${fallbackContinue}: ${continueText}`);
        } else {
          btn.removeAttribute("aria-label");
        }

        let enabled;
        if (canFinalize) {
          enabled = true;
        } else if (!isLast) {
          // After visiting the last step, the label switches to finalize everywhere but forward actions
          // must not re-enable until *all* step minimums are satisfied (avoids enabled "Create bundle"
          // that behaves like "Continue").
          enabled = visitedLast ? allMinsOk : this.#stepSatisfiesMin(this.#currentStep);
        } else {
          enabled = allMinsOk;
        }

        btn.toggleAttribute("disabled", !enabled);

        this.#updateBackCta();
      }
    }
  );
}